import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Alert,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  InputAdornment,
  IconButton,
  Chip,
  Pagination,
  Collapse,
  Tabs,
  Tab,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import backendApi from '../shared/api/backendApi';
import html2pdf from 'html2pdf.js';
import {
  INVOICE_PRINT_PROFILES,
  getInvoiceHtml2PdfOptions,
  getInvoicePrintCss,
  getStoredInvoicePrintProfile,
  normalizeInvoicePrintProfile,
  resolveInvoicePrintProfile,
  setStoredInvoicePrintProfile
} from '../shared/printProfile';
// Removed: import { generateInvoiceTemplate } from '../components/invoice-templates';

// User settings management functions (moved to database-driven approach)

// Check if html2pdf is available
console.log('🔍 html2pdf loaded:', typeof html2pdf);

// Add CSS animations
const animationStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes bounce {
    0%, 20%, 53%, 80%, 100% { transform: translate3d(0,0,0); }
    40%, 43% { transform: translate3d(0, -30px, 0); }
    70% { transform: translate3d(0, -15px, 0); }
    90% { transform: translate3d(0, -4px, 0); }
  }
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const existingStyleTag = document.getElementById('invoice-animation-styles');
  if (!existingStyleTag) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'invoice-animation-styles';
    styleSheet.type = 'text/css';
    styleSheet.innerText = animationStyles;
    document.head.appendChild(styleSheet);
  }
}

// ===== OPTIMIZED DATA LOADING SYSTEM =====

// Data cache to prevent repeated API calls
const dataCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache management
const getCachedData = (key) => {
  const cached = dataCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log(`📋 Using cached data for: ${key}`);
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  dataCache.set(key, {
    data,
    timestamp: Date.now()
  });
  console.log(`💾 Cached data for: ${key}`);
};

// Optimized retry logic with connection pooling awareness
const loadWithRetry = async (loadFunction, functionName, maxRetries = 3, options = {}) => {
  const { priority = 'normal', cacheKey = null } = options;

  // Check cache first
  if (cacheKey) {
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      console.log(`🚀 ${functionName} - Using cached data`);
      return cachedData;
    }
  }

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔄 ${functionName} - Attempt ${attempt}/${maxRetries} (Priority: ${priority})`);

      // Add small delay for non-critical requests to prevent pool exhaustion
      if (priority === 'background' && attempt === 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const result = await loadFunction();
      console.log(`✅ ${functionName} - Success on attempt ${attempt}`);

      // Cache the result if cache key provided
      if (cacheKey && result) {
        setCachedData(cacheKey, result);
      }

      return result;
    } catch (error) {
      console.warn(`⚠️ ${functionName} - Attempt ${attempt}/${maxRetries} failed:`, error.message);

      if (attempt === maxRetries) {
        console.error(`❌ ${functionName} - All ${maxRetries} attempts failed`);
        throw error;
      }

      // Exponential backoff with jitter to prevent thundering herd
      const baseDelay = Math.pow(2, attempt - 1) * 1000;
      const jitter = Math.random() * 1000; // Add up to 1 second of jitter
      const delay = baseDelay + jitter;

      console.log(`⏳ ${functionName} - Waiting ${Math.round(delay)}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

const isExpectedNoDataError = (error) => {
  const status = error?.response?.status;
  const message = `${error?.response?.data?.error || ''} ${error?.response?.data?.message || ''}`.toLowerCase();
  return (
    [400, 404].includes(status) &&
    (
      message.includes('company not found') ||
      message.includes('business owner authentication required') ||
      message.includes('authentication required') ||
      message.includes('no business found')
    )
  );
};

const getDefaultTemplateSettings = () => ({
  invoicePrefix: 'INV',
  defaultTaxRate: '18',
  salesNote: 'Thank you for your business!',
  authorizedSignature: 'Authorized Signatory',
  selectedTemplate: 'simple',
  paymentTerms: 'Net 30',
  printProfile: getStoredInvoicePrintProfile()
});

const applyTemplateSettingsToState = (baseTemplateData, settings = {}) => {
  const selectedTemplate = settings.selectedTemplate || 'simple';
  const printProfile = normalizeInvoicePrintProfile(settings.printProfile || getStoredInvoicePrintProfile());
  return {
    ...baseTemplateData,
    taxRate: settings.defaultTaxRate || '18',
    salesNote: settings.salesNote || 'Thank you for your business!',
    authorizedSignature: settings.authorizedSignature || 'Authorized Signatory',
    paymentTerms: settings.paymentTerms || 'Net 30',
    printProfile,
    invoicePrefix: settings.invoicePrefix || 'INV',
    selectedTemplate,
    theme: selectedTemplate
  };
};

const formatINR = (value) => {
  const number = Number(value || 0);
  return `₹${number.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const extractNetDays = (paymentTerms) => {
  const text = String(paymentTerms || '');
  const match = text.match(/net\s*(\d+)/i);
  return match ? Number(match[1]) : 30;
};

const parseDateLoose = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  const text = String(value).trim();
  const direct = new Date(text);
  if (!Number.isNaN(direct.getTime())) return direct;

  const normalized = text.replace(/[.-]/g, '/');
  const parts = normalized.split('/');
  if (parts.length !== 3) return null;

  const [a, b, c] = parts.map((part) => Number(part));
  if ([a, b, c].some((part) => Number.isNaN(part))) return null;

  // Supports dd/mm/yyyy and yyyy/mm/dd
  if (parts[0].length === 4) {
    const d = new Date(a, b - 1, c);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(c, b - 1, a);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateIN = (value) => {
  const d = parseDateLoose(value);
  return d ? d.toLocaleDateString('en-IN') : '--';
};

const calculateDueDateIN = (issueDate, paymentTerms) => {
  const base = parseDateLoose(issueDate) || new Date();
  const days = extractNetDays(paymentTerms);
  const due = new Date(base);
  due.setDate(due.getDate() + days);
  return due.toLocaleDateString('en-IN');
};

const getInvoiceStatusChipColor = (status) => {
  const normalized = String(status || 'unpaid').toLowerCase();
  if (normalized === 'paid') return 'success';
  if (normalized === 'partial' || normalized === 'pending' || normalized === 'sent' || normalized === 'draft' || normalized === 'viewed' || normalized === 'downloaded') return 'warning';
  if (normalized === 'overdue') return 'error';
  return 'default';
};

const getInvoiceKey = (invoice) => invoice?.id || invoice?.invoice_no || invoice?.invoiceId || `${invoice?.customer_name || ''}-${invoice?.invoice_date || ''}`;

const getEffectiveInvoiceStatus = (invoice) => {
  const totalAmount = Number(invoice?.total_amount ?? invoice?.totalAmount ?? 0);
  const totalPaid = Number(invoice?.total_paid ?? invoice?.totalPaid ?? 0);
  const dueRaw = invoice?.due_amount ?? invoice?.dueAmount;
  const computedDue = Math.max(totalAmount - totalPaid, 0);
  const dueAmount = Number.isFinite(Number(dueRaw)) ? Math.max(Number(dueRaw), 0) : computedDue;
  const rawStatus = String(invoice?.effective_status || invoice?.status || 'unpaid').toLowerCase();

  if (totalAmount > 0 && totalPaid >= totalAmount) return 'paid';
  if (totalPaid > 0 && totalPaid < totalAmount) return 'partial';
  if (dueAmount <= 0 && totalAmount > 0) return 'paid';
  if (rawStatus === 'overdue') return 'overdue';
  return rawStatus || 'unpaid';
};

function Invoices() {
  const [open, setOpen] = useState(false);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Loading invoice page...');
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [updatingTemplate, setUpdatingTemplate] = useState(false);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [success, setSuccess] = useState(null);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const initialLoadStartedRef = useRef(false);
  const [templateData, setTemplateData] = useState(() => {
    // Default template data (will be overridden by database data)
    return {
      companyName: '',
      address: '',
      phone: '',
      email: '',
      gstNumber: '',
      bankDetails: '',
      terms: '',
      logo: null,
      // New invoice-specific fields
      taxRate: '0',
      salesNote: '',
      authorizedSignature: '',
      paymentTerms: 'Net 30', // Payment terms like "Net 30" (due in 30 days)
      printProfile: getStoredInvoicePrintProfile(),
      invoicePrefix: 'INV', // Invoice prefix (e.g., INV, INV001, or business initials)
      selectedTemplate: 'simple', // Selected template from database - synced with theme
      // Unified template configuration
      theme: 'simple', // 'simple' or 'modern'
      colors: {
        primary: '#3b82f6',
        secondary: '#9333ea',
        accent: '#ec4899',
        text: '#111827',
        textSecondary: '#6b7280',
        background: '#ffffff',
        border: '#e5e7eb'
      },
      layout: {
        compact: false,
        maxWidth: '1024px',
        padding: '40px'
      },
      branding: {
        showLogo: true,
        logoUrl: null,
        logoSize: '64px'
      },
      sections: {
        showTaxBreakdown: true,
        showNotes: true,
        showSignature: true,
        showPaymentSummary: true
      },
      typography: {
        fontFamily: 'Inter, sans-serif',
        fontSize: {
          title: '36px',
          heading: '18px',
          body: '14px',
          small: '12px'
        }
      }
    };
  });
  const [invoiceParams, setInvoiceParams] = useState({
    invoiceNumber: '',
    customerName: '',
  });
  const [activeTab, setActiveTab] = useState('create');
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const [businessData, setBusinessData] = useState({
    companyName: '',
    address: '',
    phone: '',
    email: '',
    gstNumber: '',
    logoUrl: '',
    bankDetails: '',
    terms: '',
    paymentTerms: 'Net 30',
    salesNote: '',
    authorizedSignature: ''
  });
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [showCustomerInvoiceSelector, setShowCustomerInvoiceSelector] = useState(false);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [customerNames, setCustomerNames] = useState(['John Doe', 'Jane Smith', 'Bob Johnson', 'Alice Brown']); // Sample data for testing

  // Local state for input field display values (without prefix)
  const [invoiceNumberInput, setInvoiceNumberInput] = useState('');

  // Search and filtering state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [invoiceSearchSummary, setInvoiceSearchSummary] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Status tracking state
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Enhanced UI state
  const [fullScreenPreview, setFullScreenPreview] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const hasAutoPreviewAttemptedRef = useRef(false);
  const lastInvoicesLoadAtRef = useRef(0);
  const lastSalesLoadAtRef = useRef(0);

  // Track if business data has been loaded to prevent showing sample data
  const [businessDataLoaded, setBusinessDataLoaded] = useState(false);

  // Track sales data loading state separately
  const [salesLoading, setSalesLoading] = useState(false);

  const invoiceHistoryStats = useMemo(() => {
    const baseStats = {
      paid: { count: 0, amount: 0 },
      partial: { count: 0, amount: 0 },
      unpaid: { count: 0, amount: 0 },
      overdue: { count: 0, amount: 0 },
      remaining: { count: 0, amount: 0 }
    };

    // Prefer backend-provided aggregate summary for the full filtered result set.
    if (invoiceSearchSummary?.byStatus && typeof invoiceSearchSummary.byStatus === 'object') {
      Object.entries(invoiceSearchSummary.byStatus).forEach(([rawStatus, summary]) => {
        const status = String(rawStatus || '').toLowerCase();
        const count = Number(summary?.count || 0);
        const totalAmount = Number(summary?.totalAmount || 0);
        const totalDue = Number(summary?.totalDue || 0);

        if (status === 'paid') {
          baseStats.paid.count += count;
          baseStats.paid.amount += totalAmount;
          return;
        }

        if (status === 'partial') {
          baseStats.partial.count += count;
          baseStats.partial.amount += totalDue;
          baseStats.remaining.count += count;
          baseStats.remaining.amount += totalDue;
          return;
        }

        if (status === 'overdue') {
          baseStats.overdue.count += count;
          baseStats.overdue.amount += totalDue;
          baseStats.remaining.count += count;
          baseStats.remaining.amount += totalDue;
          return;
        }

        if (status === 'unpaid' || status === 'draft' || status === 'sent' || status === 'viewed' || status === 'downloaded') {
          baseStats.unpaid.count += count;
          baseStats.unpaid.amount += totalDue;
          baseStats.remaining.count += count;
          baseStats.remaining.amount += totalDue;
          return;
        }

        // Unknown statuses with due are treated as unpaid for payment summary.
        if (totalDue > 0) {
          baseStats.unpaid.count += count;
          baseStats.unpaid.amount += totalDue;
          baseStats.remaining.count += count;
          baseStats.remaining.amount += totalDue;
        }
      });

      // Use backend total due as source of truth when available.
      if (invoiceSearchSummary?.total && Number.isFinite(Number(invoiceSearchSummary.total.totalDue))) {
        baseStats.remaining.amount = Number(invoiceSearchSummary.total.totalDue || 0);
      }

      return baseStats;
    }

    // Fallback: derive from current page rows.
    invoices.forEach((invoice) => {
      const status = getEffectiveInvoiceStatus(invoice);
      const totalAmount = Number(invoice.total_amount ?? invoice.totalAmount ?? 0);
      const totalPaid = Number(invoice.total_paid ?? invoice.totalPaid ?? 0);
      const dueRaw = invoice.due_amount ?? invoice.dueAmount;
      const dueAmount = Number.isFinite(Number(dueRaw))
        ? Math.max(Number(dueRaw), 0)
        : Math.max(totalAmount - totalPaid, 0);

      if (status === 'paid') {
        baseStats.paid.count += 1;
        baseStats.paid.amount += totalAmount;
      } else if (status === 'partial') {
        baseStats.partial.count += 1;
        baseStats.partial.amount += dueAmount;
        baseStats.remaining.count += 1;
        baseStats.remaining.amount += dueAmount;
      } else if (status === 'overdue') {
        baseStats.overdue.count += 1;
        baseStats.overdue.amount += dueAmount;
        baseStats.remaining.count += 1;
        baseStats.remaining.amount += dueAmount;
      } else {
        baseStats.unpaid.count += 1;
        baseStats.unpaid.amount += dueAmount;
        baseStats.remaining.count += 1;
        baseStats.remaining.amount += dueAmount;
      }
    });

    return baseStats;
  }, [invoiceSearchSummary, invoices]);

  const recentUniqueSales = useMemo(() => {
    const groupedSales = sales.reduce((acc, sale) => {
      const key =
        sale.id ||
        sale['Invoice No'] ||
        sale.invoice_no ||
        `${sale['Customer Name'] || sale.customer_name}-${sale['Date'] || sale.invoice_date}-${sale['Total Amount'] || sale.total_amount}`;
      if (!acc[key]) {
        acc[key] = {
          ...sale,
          items: sale.items || []
        };
      } else if (sale.items && Array.isArray(sale.items)) {
        acc[key].items = [...(acc[key].items || []), ...sale.items];
      }
      return acc;
    }, {});

    return Object.values(groupedSales).slice(-10);
  }, [sales]);

  const customerInvoiceTotals = useMemo(() => {
    if (!Array.isArray(customerInvoices) || customerInvoices.length === 0) {
      return {
        totalPurchase: 0,
        totalPaid: 0,
        totalDue: 0,
        totalItems: 0,
      };
    }

    return customerInvoices.reduce(
      (acc, inv) => {
        acc.totalPurchase += Number(inv?.summary?.totalPurchase || 0);
        acc.totalPaid += Number(inv?.summary?.totalPaid || 0);
        acc.totalDue += Number(inv?.summary?.totalDue || 0);
        acc.totalItems += Number(inv?.summary?.itemsCount || 0);
        return acc;
      },
      {
        totalPurchase: 0,
        totalPaid: 0,
        totalDue: 0,
        totalItems: 0,
      }
    );
  }, [customerInvoices]);

  useEffect(() => {
    if (initialLoadStartedRef.current) {
      return;
    }
    initialLoadStartedRef.current = true;

    const loadDataSequentially = async () => {
      try {
        console.log('🔄 Starting sequential data loading for invoice page...');

        // Step 1: Load critical business data first (needed for templates)
        setLoadingMessage('Loading business information...');
        console.log('🏢 Step 1: Loading business data...');
        await loadWithRetry(loadBusinessData, 'Business Data Loading');

        // Step 2: Load template data (depends on business data for preview)
        setLoadingMessage('Loading invoice templates...');
        console.log('📄 Step 2: Loading invoice template...');
        await loadUserSettings();

        // Step 3: Load invoice search results (needed for initial display)
        setLoadingMessage('Loading invoice history...');
        console.log('📋 Step 3: Loading invoice search results...');
        await loadWithRetry(() => loadInvoices(1), 'Invoice Search Loading');

        // Step 4/5: Load sales + customers in parallel (independent requests)
        setLoadingMessage('Loading recent sales & customers...');
        console.log('📊👥 Step 4/5: Loading sales and customers in parallel...');
        await Promise.all([
          loadWithRetry(loadSalesData, 'Sales Data Loading', 3, {
            priority: 'normal',
            cacheKey: 'sales_data'
          }),
          loadWithRetry(loadCustomerNames, 'Customer Data Loading', 3, {
            priority: 'normal',
            cacheKey: 'customer_names'
          })
        ]);

        console.log('✅ All invoice page data loaded successfully');
        setLoadingMessage('Ready');

      } catch (error) {
        console.error('❌ Error in sequential data loading:', error);
        if (isExpectedNoDataError(error)) {
          setError(null);
          setInfoMessage('No invoice data yet. Generate your first invoice from recent sales.');
          setLoadingMessage('Ready');
        } else {
          setError('Failed to load page data. Please refresh the page.');
          setLoadingMessage('Error loading data');
        }
      } finally {
        // Keep loading true until all data is loaded to prevent UI flicker
        setTimeout(() => setLoading(false), 500);
      }
    };

    loadDataSequentially();
  }, []);


  // Track if we're loading from database to prevent auto-save
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Ref to track pending auto-save timeouts
  const autoSaveTimeoutRef = useRef(null);

  // Load user settings from database on component mount
  const loadUserSettings = async () => {
    try {
      setIsLoadingSettings(true);
      console.log('⚙️ Loading user settings from database...');
      const response = await backendApi.getUserSettings();

      if (response.success && response.settings) {
        // Settings exist - use them
        const dbSettings = response.settings;
        console.log('✅ User settings loaded from database:', dbSettings);
        const resolvedPrintProfile = normalizeInvoicePrintProfile(
          dbSettings.printProfile || getStoredInvoicePrintProfile()
        );
        setTemplateData((prev) => applyTemplateSettingsToState(prev, {
          ...dbSettings,
          printProfile: resolvedPrintProfile
        }));
        setStoredInvoicePrintProfile(resolvedPrintProfile);
        console.log('✅ Template data updated from database');

      } else {
        // No settings found - create defaults in database
        console.log('📝 No user settings found - creating defaults in database...');
        const defaultSettings = getDefaultTemplateSettings();
        setStoredInvoicePrintProfile(defaultSettings.printProfile);

        // Save defaults to database
        const saveResponse = await backendApi.saveUserSettings(defaultSettings);
        if (saveResponse.success) {
          console.log('✅ Default settings saved to database');
          setTemplateData((prev) => applyTemplateSettingsToState(prev, defaultSettings));
          console.log('✅ Template data set with defaults');

        } else {
          console.error('❌ Failed to save default settings');
          setTemplateData((prev) => applyTemplateSettingsToState(prev, defaultSettings));
          setInfoMessage((prev) => prev || 'No invoice settings found yet. Using defaults.');
        }
      }
    } catch (error) {
      if (isExpectedNoDataError(error)) {
        console.log('ℹ️ No user settings/business context yet - using default invoice settings');
        const fallbackSettings = getDefaultTemplateSettings();
        setStoredInvoicePrintProfile(fallbackSettings.printProfile);
        setTemplateData((prev) => applyTemplateSettingsToState(prev, fallbackSettings));
        setError(null);
        setInfoMessage((prev) => prev || 'No invoice settings found yet. Using defaults.');
        return;
      }
      console.error('❌ Failed to load/create user settings:', error);
      setError('Failed to load settings. Please refresh the page.');
    } finally {
      setIsLoadingSettings(false);
      setHasInitialized(true);
      // Clear any pending auto-save timeouts
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
        console.log('🧹 Cleared pending auto-save timeout');
      }
    }
  };

  // Save template data to database whenever it changes
  const saveUserSettings = async (newTemplateData) => {
    try {
      console.log('💾 Saving user settings to database...');

      // Map template data to database format
      const settingsToSave = {
        invoicePrefix: newTemplateData.invoicePrefix || 'INV',
        defaultTaxRate: newTemplateData.taxRate || '0',
        salesNote: newTemplateData.salesNote || '',
        authorizedSignature: newTemplateData.authorizedSignature || '',
        selectedTemplate: newTemplateData.selectedTemplate || 'simple',
        paymentTerms: newTemplateData.paymentTerms || 'Net 30',
        printProfile: normalizeInvoicePrintProfile(newTemplateData.printProfile || getStoredInvoicePrintProfile())
      };

      setStoredInvoicePrintProfile(settingsToSave.printProfile);

      const response = await backendApi.saveUserSettings(settingsToSave);
      if (response.success) {
        console.log('✅ User settings saved to database successfully');
      } else {
        console.error('❌ Failed to save user settings to database');
      }
    } catch (error) {
      console.error('❌ Error saving user settings to database:', error);
    }
  };

  // Refresh invoice/sales data on return to page (focus/tab visible) when stale
  useEffect(() => {
    const STALE_MS = 5 * 60 * 1000;

    const refreshOnReturn = async () => {
      if (loading) return;

      const invoicesStale =
        !Array.isArray(invoices) ||
        invoices.length === 0 ||
        !lastInvoicesLoadAtRef.current ||
        (Date.now() - lastInvoicesLoadAtRef.current) > STALE_MS;

      const salesStale =
        !Array.isArray(sales) ||
        sales.length === 0 ||
        !lastSalesLoadAtRef.current ||
        (Date.now() - lastSalesLoadAtRef.current) > STALE_MS;

      if (invoicesStale) {
        console.log('Refreshing invoice history after return...');
        try {
          await loadInvoices(currentPage || 1);
        } catch (error) {
          console.warn('Failed to refresh invoices on return:', error.message);
        }
      }

      if (salesStale) {
        console.log('Refreshing sales data after return...');
        try {
          await loadSalesData();
        } catch (error) {
          console.warn('Failed to refresh sales on return:', error.message);
        }
      }
    };

    refreshOnReturn();

    const handleFocus = () => {
      refreshOnReturn();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshOnReturn();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [loading, sales, invoices, currentPage, searchTerm, statusFilter, dateFrom, dateTo]);

  // Auto-save disabled - only save explicitly when user updates template
  // or when creating defaults for first-time users

  // Preview is now only generated when user explicitly requests it (Generate Invoice button)
  // Removed automatic preview generation on template/business data changes


  // Success animation when invoice is generated
  useEffect(() => {
    if (generatedInvoice) {
      setShowSuccessAnimation(true);
      const timer = setTimeout(() => setShowSuccessAnimation(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [generatedInvoice]);

  useEffect(() => {
    if (activeTab !== 'history') return;

    if (!invoices.length) {
      setSelectedInvoice(null);
      return;
    }

    const selectedKey = getInvoiceKey(selectedInvoice);
    const stillExists = selectedKey
      ? invoices.some((invoice) => getInvoiceKey(invoice) === selectedKey)
      : false;

    if (!stillExists) {
      setSelectedInvoice(invoices[0]);
    }
  }, [activeTab, invoices, selectedInvoice]);

  // Create a lightweight initial preview so the screen never feels blank.
  useEffect(() => {
    if (loading || activeTab !== 'create') return;
    if (showInvoice || previewInvoice || generatingInvoice) return;
    if (hasAutoPreviewAttemptedRef.current) return;
    if (!templateData?.selectedTemplate) return;

    hasAutoPreviewAttemptedRef.current = true;
    generateTemplatePreview();
  }, [loading, activeTab, showInvoice, previewInvoice, generatingInvoice, templateData?.selectedTemplate]);

  const loadSalesData = async () => {
    try {
      setSalesLoading(true);
      const response = await loadWithRetry(
        () => backendApi.getSales(),
        'Sales Data Loading',
        3,
        { priority: 'normal', cacheKey: null } // Don't cache to ensure fresh data
      );
      // The backend API returns an object with sales array
      const data = response.sales || response || [];

      console.log(`✅ Loaded ${data.length} sales from database`);

      // Sort sales by ID to ensure consistent invoice number mapping
      data.sort((a, b) => (a.id || 0) - (b.id || 0));
      console.log(`🔄 Sorted ${data.length} sales by ID for consistent invoice numbering`);

      // Check invoice status for each sale using the dedicated API
      if (data.length > 0) {
        try {
          const saleIds = data.map(sale => sale.id);
          console.log(`🔍 Checking invoice status for ${saleIds.length} sales...`);

          const statusResponse = await loadWithRetry(
            () => backendApi.checkInvoiceStatus(saleIds),
            'Invoice Status Check',
            3,
            { priority: 'normal' }
          );

          if (statusResponse.success && statusResponse.status) {
            console.log(`✅ Invoice status check completed for ${statusResponse.status.length} sales`);

            // Merge invoice status with sales data
            const salesWithStatus = data.map(sale => {
              const statusInfo = statusResponse.status.find(s => s.sale_id === sale.id);
              return {
                ...sale,
                invoice_generated: statusInfo ? statusInfo.invoice_generated : false,
                existing_invoice: statusInfo && statusInfo.generated_invoice_id ? {
                  invoice_no: statusInfo.generated_invoice_id,
                  pdf_path: statusInfo.pdf_path,
                  whatsapp_link: statusInfo.whatsapp_link
                } : null,
                invoice_check_performed: true,
                invoice_check_timestamp: new Date().toISOString()
              };
            });

            const generatedCount = salesWithStatus.filter(s => s.invoice_generated).length;
            console.log(`📈 ${generatedCount} out of ${salesWithStatus.length} sales have invoices generated`);

            setSales(salesWithStatus);
            lastSalesLoadAtRef.current = Date.now();
            // Update customer names from sales data
            const salesCustomerNames = [...new Set(
              salesWithStatus
                .map(sale => sale.customer_name)
                .filter(name => name && name.trim() !== '')
                .map(name => name.trim())
            )].sort();
            setCustomerNames(salesCustomerNames);
            return salesWithStatus;
          } else {
            console.warn('⚠️ Invoice status check failed, using default status');
            // Fallback: assume no invoices generated
            const salesWithStatus = data.map(sale => ({
              ...sale,
              invoice_generated: false,
              existing_invoice: null,
              invoice_check_performed: false,
              invoice_check_error: 'Status check failed',
              invoice_check_timestamp: new Date().toISOString()
            }));

            setSales(salesWithStatus);
            lastSalesLoadAtRef.current = Date.now();
            // Update customer names from sales data
            const salesCustomerNames = [...new Set(
              salesWithStatus
                .map(sale => sale.customer_name)
                .filter(name => name && name.trim() !== '')
                .map(name => name.trim())
            )].sort();
            console.log('👥 Updated customer names from sales data (fallback):', salesCustomerNames);
            setCustomerNames(salesCustomerNames);
            return salesWithStatus;
          }
        } catch (statusError) {
          console.warn('⚠️ Invoice status check error:', statusError.message);
          // Fallback: assume no invoices generated
          const salesWithStatus = data.map(sale => ({
            ...sale,
            invoice_generated: false,
            existing_invoice: null,
            invoice_check_performed: false,
            invoice_check_error: statusError.message,
            invoice_check_timestamp: new Date().toISOString()
          }));

          setSales(salesWithStatus);
          lastSalesLoadAtRef.current = Date.now();
          // Update customer names from sales data
          const salesCustomerNames = [...new Set(
            salesWithStatus
              .map(sale => sale.customer_name)
              .filter(name => name && name.trim() !== '')
              .map(name => name.trim())
          )].sort();
          console.log('👥 Updated customer names from sales data:', salesCustomerNames);
          setCustomerNames(salesCustomerNames);
          return salesWithStatus;
        }
      } else {
        console.log('ℹ️ No sales data to check invoice status for');
        setSales([]);
        lastSalesLoadAtRef.current = Date.now();
        return [];
      }
    } catch (err) {
      console.error('Error loading sales:', err);
      throw err; // Re-throw to let loadWithRetry handle it
    } finally {
      setSalesLoading(false);
    }
  };

  // Function to refresh sales data (used after invoice generation)
  const refreshSalesData = async () => {
    try {
      console.log('🔄 Refreshing sales data after invoice generation...');
      await loadSalesData();
      console.log('✅ Sales data refreshed successfully');
    } catch (error) {
      console.warn('⚠️ Failed to refresh sales data:', error.message);
    }
  };

  const loadCustomerNames = async () => {
    try {
      console.log('👥 Loading customer names...');

      // First, try to get customer names from sales data (most reliable for invoice generation)
      if (sales.length > 0) {
        const salesCustomers = [...new Set(
          sales
            .map(sale => sale.customer_name)
            .filter(name => name && name.trim() !== '')
            .map(name => name.trim())
        )].sort();

        if (salesCustomers.length > 0) {
          console.log('✅ Loaded customer names from sales data:', salesCustomers);
          setCustomerNames(salesCustomers);
          return salesCustomers;
        }
      }

      // Fallback to customers table
      console.log('📋 Sales data not available, loading from customers table...');
      const response = await loadWithRetry(
        () => backendApi.getCustomers(),
        'Customer Data Loading',
        3,
        { priority: 'background', cacheKey: 'customer_names' }
      );
      const customerData = response.customers || response || [];
      console.log('📋 Customer table data:', customerData);

      if (customerData && customerData.length > 0) {
        // Extract unique customer names from the database response
        const uniqueCustomers = [...new Set(
          customerData
            .map(customer => customer.name || customer.customer_name)
            .filter(name => name && name.trim() !== '')
            .map(name => name.trim())
        )].sort();
        setCustomerNames(uniqueCustomers);
        console.log('✅ Loaded customer names from table:', uniqueCustomers);
        return uniqueCustomers;
      } else {
        console.log('⚠️ No customer data found anywhere');
        setCustomerNames([]);
        return [];
      }
    } catch (err) {
      console.error('❌ Error loading customer names:', err);
      // Final fallback to sales data
      const salesCustomers = [...new Set(
        sales
          .map(sale => sale.customer_name)
          .filter(name => name && name.trim() !== '')
          .map(name => name.trim())
      )].sort();
      setCustomerNames(salesCustomers);
      console.log('🔄 Fallback to sales data customer names:', salesCustomers);
      return salesCustomers;
    }
  };

  const loadBusinessData = async () => {
    try {
      console.log('🏢 Loading business data from database...');

      // Use optimized loading with caching
      const response = await loadWithRetry(
        () => backendApi.getBusinessOverview(),
        'Business Data Loading',
        3,
        { priority: 'critical', cacheKey: 'business_overview' }
      );

      console.log('📋 Raw API response:', response);

      // The backend returns the business data in the business object
      const businessOverview = response.business || response.overview || response || {};
      console.log('📋 Business overview data:', businessOverview);
      console.log('📋 Business overview keys:', Object.keys(businessOverview));

      const mappedBusinessData = {
        companyName: businessOverview.business_name || businessOverview.name || businessOverview.companyName || '',
        name: businessOverview.business_name || businessOverview.name || businessOverview.companyName || '',
        address: businessOverview.address || businessOverview.business_address || '',
        phone: businessOverview.phone || businessOverview.businessPhone || businessOverview.business_phone || '',
        businessPhone: businessOverview.phone || businessOverview.businessPhone || businessOverview.business_phone || '',
        email: businessOverview.email || businessOverview.business_email || '',
        gstNumber: businessOverview.gst_number || businessOverview.gst || businessOverview.gstNumber || '',
        gst: businessOverview.gst_number || businessOverview.gst || businessOverview.gstNumber || '',
        businessType: businessOverview.business_type || businessOverview.type || businessOverview.businessType || '',
        type: businessOverview.business_type || businessOverview.type || businessOverview.businessType || '',
        industry: businessOverview.industry || '',
        website: businessOverview.website || '',
        city: businessOverview.city || '',
        state: businessOverview.state || '',
        zipCode: businessOverview.zip_code || businessOverview.zipCode || '',
        country: businessOverview.country || 'India',
        description: businessOverview.description || '',
        employeeCount: businessOverview.employee_count || businessOverview.employeeCount || '',
        foundedYear: businessOverview.founded_year || businessOverview.foundedYear || '',
        logoUrl: businessOverview.logo_url || businessOverview.logoUrl || '',
        terms: businessOverview.terms || 'Payment due within 30 days',
        paymentTerms: businessOverview.paymentTerms || 'Net 30',
        salesNote: businessOverview.salesNote || '',
        authorizedSignature: businessOverview.authorizedSignature || ''
      };

      // Treat a real company name as valid business data even if other fields are missing.
      const companyName = (mappedBusinessData.companyName || '').trim();
      const hasRealBusinessData =
        companyName.length > 0 &&
        companyName.toLowerCase() !== 'your business name';

      if (hasRealBusinessData) {
        console.log('✅ Real business data loaded:', mappedBusinessData);
        console.log('✅ Setting businessData state with real data');
      } else {
        console.log('ℹ️ No business profile data found yet');
      }

      setBusinessData(mappedBusinessData);
      setBusinessDataLoaded(true);
      return mappedBusinessData;
    } catch (err) {
      if (isExpectedNoDataError(err)) {
        console.log('ℹ️ No business profile found yet - using empty invoice business state');
        const emptyBusinessData = {
          companyName: '',
          name: '',
          address: '',
          phone: '',
          businessPhone: '',
          email: '',
          gstNumber: '',
          gst: '',
          businessType: '',
          type: '',
          industry: '',
          website: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'India',
          description: '',
          employeeCount: '',
          foundedYear: '',
          logoUrl: '',
          terms: 'Payment due within 30 days',
          paymentTerms: 'Net 30',
          salesNote: '',
          authorizedSignature: ''
        };
        setBusinessData(emptyBusinessData);
        setBusinessDataLoaded(true);
        return emptyBusinessData;
      }

      console.error('❌ Error loading business data:', err);
      console.error('❌ Error details:', err.message, err.stack);
      console.log('🔄 Using sample business data as fallback due to error');

      // Return sample data instead of null so the invoice can still function
      const sampleBusinessData = {
        companyName: 'Your Business Name',
        name: 'Your Business Name',
        address: 'Your Business Address',
        phone: 'Your Phone Number',
        businessPhone: 'Your Phone Number',
        email: 'your@email.com',
        gstNumber: '',
        gst: '',
        businessType: '',
        type: '',
        industry: '',
        website: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'India',
        description: '',
        employeeCount: '',
        foundedYear: '',
        logoUrl: '',
        terms: 'Payment due within 30 days',
        paymentTerms: 'Net 30',
        salesNote: '',
        authorizedSignature: ''
      };
      setBusinessData(sampleBusinessData);
      setBusinessDataLoaded(true);
      return sampleBusinessData;
    }
  };

  const loadInvoices = async (page = 1, overrides = {}) => {
    try {
      setInvoicesLoading(true);
      setError(null);
      setInfoMessage(null);

      const effectiveSearchTerm = overrides.searchTerm ?? searchTerm;
      const effectiveStatusFilter = overrides.statusFilter ?? statusFilter;
      const effectiveDateFrom = overrides.dateFrom ?? dateFrom;
      const effectiveDateTo = overrides.dateTo ?? dateTo;

      const searchParams = {
        search: effectiveSearchTerm || undefined,
        status: effectiveStatusFilter || undefined,
        dateFrom: effectiveDateFrom || undefined,
        dateTo: effectiveDateTo || undefined,
        limit: 20,
        offset: (page - 1) * 20
      };

      console.log('Searching invoices with params:', searchParams);

      const response = await loadWithRetry(
        () => backendApi.searchInvoices(searchParams),
        'Invoice Search Loading',
        3,
        {
          priority: 'normal',
          cacheKey: `invoices_search_v2_${JSON.stringify(searchParams)}`
        }
      );

      if (response.success) {
        const invoiceRows = response.invoices || [];
        const totalRows = response.pagination?.total || 0;
        setInvoices(invoiceRows);
        setTotalInvoices(totalRows);
        setInvoiceSearchSummary(response.statusSummary || null);
        setCurrentPage(page);
        lastInvoicesLoadAtRef.current = Date.now();
        console.log(`Loaded ${invoiceRows.length} invoices`);

        if (invoiceRows.length === 0) {
          if (effectiveSearchTerm || effectiveStatusFilter || effectiveDateFrom || effectiveDateTo) {
            setInfoMessage('No invoices match your current filters.');
          } else {
            setInfoMessage('No invoices yet. Generate your first invoice from recent sales.');
          }
        }
      } else {
        setInvoiceSearchSummary(null);
        setError('Failed to load invoices');
      }
    } catch (err) {
      if (isExpectedNoDataError(err)) {
        setInvoices([]);
        setTotalInvoices(0);
        setInvoiceSearchSummary(null);
        setCurrentPage(1);
        lastInvoicesLoadAtRef.current = Date.now();
        setError(null);
        if (effectiveSearchTerm || effectiveStatusFilter || effectiveDateFrom || effectiveDateTo) {
          setInfoMessage('No invoices match your current filters.');
        } else {
          setInfoMessage('No invoices yet. Generate your first invoice from recent sales.');
        }
      } else {
        setInvoiceSearchSummary(null);
        setError('Failed to search invoices. Please try again.');
        console.error('Error searching invoices:', err);
      }
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadInvoices(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
    loadInvoices(1, {
      searchTerm: '',
      statusFilter: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handlePageChange = (event, page) => {
    loadInvoices(page);
  };

  // DISABLED: Complex status updates not needed for current workflow
  // const handleStatusUpdate = async () => {
  //   if (!selectedInvoice || !newStatus) return;
  //
  //   try {
  //     setUpdatingStatus(true);
  //     setError(null);
  //
  //     console.log('🔄 Updating invoice status:', selectedInvoice.id, newStatus);
  //
  //     const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/invoices/${selectedInvoice.id}/status`, {
  //       method: 'PUT',
  //       headers: {
  //         'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
  //         'Content-Type': 'application/json'
  //       },
  //       body: JSON.stringify({
  //         status: newStatus,
  //         notes: statusNotes,
  //         changedBy: 'user' // You can get this from user context
  //       })
  //     });
  //
  //     const result = await response.json();
  //
  //     if (result.success) {
  //       setSuccess('Invoice status updated successfully!');
  //       setShowStatusDialog(false);
  //       setNewStatus('');
  //       setStatusNotes('');
  //       setSelectedInvoice(null);
  //
  //       // Refresh the invoices list
  //       loadInvoices(currentPage);
  //
  //       setTimeout(() => setSuccess(null), 3000);
  //     } else {
  //       setError(result.message || 'Failed to update status');
  //     }
  //   } catch (err) {
  //     setError('Failed to update invoice status. Please try again.');
  //     console.error('Error updating status:', err);
  //   } finally {
  //     setUpdatingStatus(false);
  //   }
  // };

  // DISABLED: Complex history tracking not needed for current workflow
  // const handleViewHistory = async (invoice) => {
  //   setSelectedInvoice(invoice);
  //   setLoadingHistory(true);
  //   setShowHistoryDialog(true);
  //
  //   try {
  //     console.log('📖 Loading invoice history:', invoice.id);
  //
  //     // Use the correct API endpoint structure
  //     const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/invoices/${invoice.id}/history`, {
  //       method: 'GET',
  //       headers: {
  //         'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
  //         'Content-Type': 'application/json'
  //       }
  //     });
  //
  //     // Check if response is valid JSON before parsing
  //     const contentType = response.headers.get('content-type');
  //     if (!contentType || !contentType.includes('application/json')) {
  //       throw new Error('Invalid response format - expected JSON');
  //     }
  //
  //     const result = await response.json();
  //
  //     if (result.success) {
  //       setInvoiceHistory(result.history || []);
  //     } else {
  //       setError(result.message || 'Failed to load invoice history');
  //     }
  //   } catch (err) {
  //     setError('Failed to load invoice history. Please try again.');
  //     console.error('Error loading history:', err);
  //   } finally {
  //     setLoadingHistory(false);
  //   }
  // };

  // NEW: Simple WhatsApp share functionality
  const handleShareOnWhatsApp = async (invoice) => {
    try {
      const totalAmount = typeof invoice.total_amount === 'string'
        ? parseFloat(invoice.total_amount) || 0
        : (invoice.total_amount || 0);

      let businessName = 'Your Business Name';
      if (invoice.business_name) {
        businessName = invoice.business_name;
      } else if (invoice.company_name) {
        businessName = invoice.company_name;
      } else if (businessData.companyName) {
        businessName = businessData.companyName;
      } else if (businessData.name) {
        businessName = businessData.name;
      }

      if (invoice?.id) {
        try {
          await backendApi.sendInvoicePdfToCustomer(invoice.id, {
            caption: `Invoice ${invoice.invoice_no || invoice.id}`,
            footer: businessName,
            full: 1
          });
          setSuccess(`Invoice PDF sent on WhatsApp to ${invoice.customer_name || 'customer'}`);
          setTimeout(() => setSuccess(''), 3000);
          return;
        } catch (apiError) {
          console.warn('WhatsApp invoice PDF send failed:', apiError);
          const apiMessage = apiError?.response?.data?.message || apiError?.message || 'Failed to send invoice PDF on WhatsApp.';
          setError(apiMessage);
          setTimeout(() => setError(''), 5000);
          return;
        }
      }

      setError('Invoice ID missing. Unable to send PDF.');
      setTimeout(() => setError(''), 4000);
    } catch (error) {
      console.error('Error preparing WhatsApp share:', error);
      setError('Failed to prepare WhatsApp message');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDownloadInvoicePdf = async (invoice) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_API_URL || 'https://dhandhapaani.store';
      const authToken = localStorage.getItem('authToken');

      const response = await fetch(`${backendUrl}/api/invoices/${invoice.id}/download-pdf`, {
        method: 'GET',
        headers: {
          'Authorization': authToken ? `Bearer ${authToken}` : '',
        }
      });

      if (!response.ok) {
        throw new Error(`PDF download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${invoice.invoice_no || invoice.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess('PDF downloaded successfully!');
      setTimeout(() => setSuccess(null), 2000);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download PDF. Please try again.');
    }
  };

  const handlePrintInvoicePdf = async (invoice) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_API_URL || 'https://dhandhapaani.store';
      const authToken = localStorage.getItem('authToken');

      const response = await fetch(`${backendUrl}/api/invoices/${invoice.id}/download-pdf`, {
        method: 'GET',
        headers: {
          'Authorization': authToken ? `Bearer ${authToken}` : '',
        }
      });

      if (!response.ok) {
        throw new Error(`PDF print failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
    } catch (error) {
      console.error('Error printing PDF:', error);
      setError('Failed to print PDF. Please try again.');
    }
  };

  // DISABLED: Complex status dialog not needed
  // const openStatusDialog = (invoice) => {
  //   setSelectedInvoice(invoice);
  //   setNewStatus(invoice.status || 'unpaid');
  //   setStatusNotes('');
  //   setShowStatusDialog(true);
  // };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setTemplateData({
      ...templateData, // Keep existing values
      // Reset only the essential fields
      taxRate: '0',
      salesNote: '',
      authorizedSignature: '',
      paymentTerms: 'Net 30',
      printProfile: templateData.printProfile || getStoredInvoicePrintProfile(),
      invoicePrefix: 'INV',
    });
  };

  const handleTemplateChange = async (field, value) => {
    const normalizedValue = field === 'printProfile' ? normalizeInvoicePrintProfile(value) : value;

    // Update template data first
    const updatedTemplateData = {
      ...templateData,
      [field]: normalizedValue,
    };

    // When theme changes, also update selectedTemplate to keep them in sync
    if (field === 'theme') {
      updatedTemplateData.selectedTemplate = normalizedValue;
    }

    if (field === 'printProfile') {
      setStoredInvoicePrintProfile(normalizedValue);
    }

    setTemplateData(updatedTemplateData);

    // Auto-generate preview when theme or print profile changes.
    if (field === 'theme' || field === 'printProfile') {
      console.log('🎨 Template setting changed:', field, '=', normalizedValue, '- generating preview...');
      // Pass the updated template data directly to avoid state timing issues
      generateTemplatePreview(updatedTemplateData);
    }
  };

  const handleViewInvoicePdf = async (invoice) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_API_URL || 'https://dhandhapaani.store';
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('accessToken');

      const response = await fetch(`${backendUrl}/api/invoices/${invoice.id}/download-pdf`, {
        method: 'GET',
        headers: {
          'Authorization': authToken ? `Bearer ${authToken}` : '',
        }
      });

      if (!response.ok) {
        throw new Error(`PDF view failed: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        // Popup blocked, fallback to download behavior.
        const a = document.createElement('a');
        a.href = url;
        a.download = `Invoice-${invoice.invoice_no || invoice.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    } catch (error) {
      console.error('Error viewing PDF:', error);
      setError('Failed to open PDF. Please try again.');
    }
  };

  const handleGenerateInvoiceFromRecentSale = async (saleRow, forceRegenerate = false) => {
    // Use the sale data directly
    const rowNumber = saleRow.id; // Use the actual sale ID from database

    // Use invoice number from database or generate from sale ID
    const invoiceNumber = saleRow.invoice_no || `${templateData.invoicePrefix || 'INV'}${saleRow.id.toString().padStart(3, '0')}`;

    setInvoiceParams({ invoiceNumber: invoiceNumber, customerName: '' });
    setInvoiceNumberInput(invoiceNumber.replace(/^[A-Z]+/, ''));

    try {
      setGeneratingInvoice(true);
      setError(null);

      const actionText = forceRegenerate ? 'Regenerating' : (saleRow.invoice_generated ? 'Loading' : 'Generating');
      console.log(`Invoice action (${actionText}) for recent sale:`, invoiceNumber);

      const realInvoiceData = await prepareRealInvoiceData(saleRow, rowNumber);

      const invoiceData = {
        rowNumber,
        saleRow,
        templateData: {
          ...templateData,
          forceRegenerate
        },
        selectedTemplate: templateData.theme || 'simple'
      };

      const result = await backendApi.generateInvoice(invoiceData);

      if (result.invoice) {
        setGeneratedInvoice({
          ...result.invoice,
          htmlTemplate: result.invoice.htmlTemplate || ''
        });
        setShowInvoice(true);
      }

      const successMessage = forceRegenerate
        ? 'Invoice regenerated successfully!'
        : (result?.invoice?.reusedExisting ? 'Existing invoice opened.' : 'Invoice generated successfully!');
      setSuccess(successMessage);
      setTimeout(() => setSuccess(null), 3000);

      await refreshSalesData();
    } catch (err) {
      setError('Failed to process invoice. Please try again.');
      console.error('Error processing invoice:', err);
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const handleInvoiceParamChange = (field, value) => {
    console.log('🎯 handleInvoiceParamChange called:', field, '=', value);
    setInvoiceParams(prev => {
      const newParams = {
        ...prev,
        [field]: value,
      };
      console.log('🎯 New invoiceParams:', newParams);
      return newParams;
    });
  };

  const handleUpdateTemplate = async () => {
    try {
      setUpdatingTemplate(true);
      setError(null);
      setSuccess(null);

      console.log('🔄 Updating invoice template...');

      // Save to database (this will be handled by the useEffect hook)
      await saveUserSettings(templateData);

      console.log('✅ Template updated successfully in database');
      setSuccess('Invoice template updated successfully!');

      // Close the dialog
      setOpen(false);

      // Regenerate preview with updated template data
      if (showPreview && previewInvoice) {
        // Ensure we have valid invoice data for the template
        const invoiceData = previewInvoice.invoiceData || {
          invoiceId: `PREVIEW-${Date.now()}`,
          date: new Date().toLocaleDateString('en-IN'),
          customerName: 'Sample Customer',
          phone: '9876543210',
          items: [
            { name: 'Sample Item 1', quantity: 2, price: 500, total: 1000 },
            { name: 'Sample Item 2', quantity: 1, price: 750, total: 750 }
          ],
          subtotal: 1750,
          discount: 175,
          totalAmount: 1575,
          totalPaid: 1000,
          dueAmount: 575,
          paymentMode: 'Cash',
          salesNote: templateData.salesNote || '',
          authorizedSignature: templateData.authorizedSignature || '',
          paymentTerms: templateData.paymentTerms || 'Net 30'
        };

        // Regenerate preview using backend API
        await generateTemplatePreview();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error updating template:', err);
      setError('Failed to update template. Please try again.');
    } finally {
      setUpdatingTemplate(false);
    }
  };

  const generateTemplatePreview = async (customTemplateData = null) => {
    try {
      setError(null);
      console.log('🎨 Generating preview using backend API');

      // Ensure settings are loaded first
      if (!templateData.selectedTemplate) {
        console.log('🎨 Settings not loaded - loading first...');
        await loadUserSettings();
        // Wait a bit for state to update
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      if (!templateData.selectedTemplate) {
        setError('Unable to load template settings. Please refresh the page.');
        return;
      }

      // Use provided template data or fall back to current state
      const currentTemplateData = customTemplateData || templateData;

      // Check if we have real business data loaded (not just default values)
      // Real business data should have meaningful values that are not the default fallbacks
      const hasRealBusinessData = businessData.companyName &&
        businessData.companyName.trim() !== '' &&
        businessData.companyName !== 'Your Business Name' &&
        businessData.address &&
        businessData.address.trim() !== '' &&
        businessData.address !== 'Your Business Address';

      const previewBusinessData = hasRealBusinessData ? businessData : {
        companyName: 'Your Business Name',
        name: 'Your Business Name',
        address: 'Your Business Address',
        phone: 'Your Phone Number',
        email: 'your@email.com',
        gstNumber: '',
        logoUrl: '',
        bankDetails: '',
        terms: 'Payment due within 30 days',
        paymentTerms: 'Net 30',
        salesNote: '',
        authorizedSignature: ''
      };

      console.log('🎨 Business data check:', {
        hasRealBusinessData,
        companyName: businessData.companyName,
        address: businessData.address,
        isDefaultCompanyName: businessData.companyName === 'Your Business Name',
        isDefaultAddress: businessData.address === 'Your Business Address'
      });

      console.log('🎨 Preview generation - Business data available:', hasRealBusinessData, 'Data:', previewBusinessData);
      console.log('🎨 Preview generation - Template data:', currentTemplateData);

      // Generate a sample invoice for preview (always use sample data for preview)
      const sampleInvoiceData = {
        invoiceId: `PREVIEW-${Date.now()}`,
        date: new Date().toLocaleDateString('en-IN'),
        customerName: 'Sample Customer',
        customer_name: 'Sample Customer', // For template compatibility
        phone: '9876543210',
        customer_phone: '9876543210', // For template compatibility
        items: [
          { name: 'Sample Item 1', quantity: 2, price: 500, total: 1000 },
          { name: 'Sample Item 2', quantity: 1, price: 750, total: 750 }
        ],
        subtotal: 1750,
        discount: 175, // 10% discount
        totalAmount: 1575,
        totalPaid: 1000,
        dueAmount: 575,
        paymentMode: 'Cash',
        salesNote: currentTemplateData.salesNote || '',
        authorizedSignature: currentTemplateData.authorizedSignature || '',
        paymentTerms: currentTemplateData.paymentTerms || 'Net 30'
      };

      // Call backend API for preview generation
      const response = await backendApi.generateInvoicePreview({
        invoiceData: sampleInvoiceData,
        businessData: previewBusinessData,
        templateData: currentTemplateData
      });

      if (response.success && response.html) {
        setPreviewInvoice({
          ...sampleInvoiceData,
          htmlTemplate: response.html,
          template: currentTemplateData.theme || 'simple'
        });
        setShowPreview(true);
        console.log('✅ Template preview generated successfully via backend API');
      } else {
        throw new Error('Backend preview generation failed');
      }
    } catch (err) {
      setError('Failed to generate template preview. Please try again.');
      console.error('Error generating template preview:', err);
    }
  };

  // Generate preview with real data when invoice parameters are provided
  const generatePreviewWithRealData = async () => {
    try {
      setError(null);
      console.log('🎯 Generating preview with real data based on invoice parameters:', invoiceParams);

      // Check if we have invoice parameters
      if (!invoiceParams.invoiceNumber && !invoiceParams.customerName) {
        console.log('ℹ️ No invoice parameters provided, using sample preview');
        generateTemplatePreview();
        return;
      }

      // Ensure settings are loaded first
      if (!templateData.selectedTemplate) {
        console.log('🎨 Settings not loaded - loading first...');
        await loadUserSettings();
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      if (!templateData.selectedTemplate) {
        setError('Unable to load template settings. Please refresh the page.');
        return;
      }

      let realInvoiceData = null;
      let saleRow = null;
      let rowNumber = null;

      // Try to find real data based on input parameters
      if (invoiceParams.invoiceNumber) {
        // Parse invoice number to find matching sale
        const prefix = templateData.invoicePrefix || 'INV';
        const invoiceNum = invoiceParams.invoiceNumber.trim().toUpperCase();

        if (invoiceNum.startsWith(prefix)) {
          const numericPart = invoiceNum.substring(prefix.length);
          if (numericPart && /^\d+$/.test(numericPart)) {
            const invoiceNumber = parseInt(numericPart);

            // Find sale by invoice number - prioritize exact matches first
            let matchingSale = null;
    
            // First, try to find by exact invoice_no match (for already generated invoices)
            matchingSale = sales.find(sale => sale.invoice_no === invoiceNum);
    
            // If not found, use index-based lookup (since sales are now sorted by ID)
            // Invoice number "MA002" should find the sale at index 1 (2-1)
            if (!matchingSale) {
              const saleIndex = invoiceNumber - 1;
              if (saleIndex >= 0 && saleIndex < sales.length) {
                matchingSale = sales[saleIndex];
                console.log(`📊 Found sale by index lookup: invoice ${invoiceNum} -> sale at index ${saleIndex} (ID: ${matchingSale.id}, invoice_no: ${matchingSale.invoice_no}, customer: ${matchingSale.customer_name})`);
                console.log(`📊 Sales array sample:`, sales.slice(0, 3).map(s => ({ id: s.id, invoice_no: s.invoice_no, customer: s.customer_name })));
              } else {
                console.log(`❌ Index ${saleIndex} out of bounds for sales array length ${sales.length}`);
              }
            }

            if (matchingSale) {
              saleRow = matchingSale;
              rowNumber = matchingSale.id || (sales.indexOf(matchingSale) + 1);
              realInvoiceData = await prepareRealInvoiceData(saleRow, rowNumber);
              console.log('✅ Found real data for invoice number preview:', invoiceNum, 'Sale:', saleRow);
              console.log('✅ Sale has invoice_generated:', matchingSale.invoice_generated);
              console.log('✅ Sale has existing_invoice:', !!matchingSale.existing_invoice);
            } else {
              console.log('❌ No matching sale found for invoice number:', invoiceNum);
              console.log('❌ Available sales IDs:', sales.map(s => s.id));
              console.log('❌ Available invoice numbers:', sales.map(s => s.invoice_no).filter(Boolean));
            }
          }
        }
      } else if (invoiceParams.customerName) {
        // Find sales for this customer
        const customerSales = sales.filter(sale => {
          const customerName = (sale.customer_name || '').trim().toLowerCase();
          return customerName === invoiceParams.customerName.trim().toLowerCase();
        });

        if (customerSales.length === 1) {
          // Single sale - use it for preview
          saleRow = customerSales[0];
          rowNumber = sales.indexOf(saleRow) + 2;
          realInvoiceData = await prepareRealInvoiceData(saleRow, rowNumber);
          console.log('✅ Found real data for customer preview:', invoiceParams.customerName, 'Sale:', saleRow);
        } else if (customerSales.length > 1) {
          // Multiple sales - use the first one for preview
          saleRow = customerSales[0];
          rowNumber = sales.indexOf(saleRow) + 2;
          realInvoiceData = await prepareRealInvoiceData(saleRow, rowNumber);
          console.log('✅ Found real data for customer preview (first sale):', invoiceParams.customerName, 'Sales found:', customerSales.length, 'Using sale:', saleRow);
        } else {
          console.log('❌ No sales found for customer:', invoiceParams.customerName);
        }
      }

      // If we have real data, use it for preview
      if (realInvoiceData) {
        const hasRealBusinessData = businessData.companyName &&
          businessData.companyName.trim() !== '' &&
          businessData.companyName !== 'Your Business Name' &&
          businessData.address &&
          businessData.address.trim() !== '' &&
          businessData.address !== 'Your Business Address';

        const previewBusinessData = hasRealBusinessData ? businessData : {
          companyName: 'Your Business Name',
          name: 'Your Business Name',
          address: 'Your Business Address',
          phone: 'Your Phone Number',
          email: 'your@email.com',
          gstNumber: '',
          logoUrl: '',
          bankDetails: '',
          terms: 'Payment due within 30 days',
          paymentTerms: 'Net 30',
          salesNote: '',
          authorizedSignature: ''
        };

        // Call backend API for preview generation with real data
        const response = await backendApi.generateInvoicePreview({
          invoiceData: realInvoiceData,
          businessData: previewBusinessData,
          templateData: templateData
        });

        if (response.success && response.html) {
          setPreviewInvoice({
            ...realInvoiceData,
            htmlTemplate: response.html,
            template: templateData.theme || 'simple'
          });
          setShowPreview(true);
          console.log('✅ Real data preview generated successfully');
        } else {
          throw new Error('Backend preview generation failed');
        }
      } else {
        // Fall back to sample preview
        console.log('ℹ️ No real data found, using sample preview');
        generateTemplatePreview();
      }
    } catch (err) {
      console.error('Error generating real data preview:', err);
      // Fall back to sample preview on error
      generateTemplatePreview();
    }
  };

  const handleGenerateInvoice = async () => {
    try {
      setGeneratingInvoice(true);
      setError(null);
      console.log('🎯 FRONTEND: Starting invoice generation with selected template');
      console.log('🎯 FRONTEND: invoiceParams:', invoiceParams);

      let realInvoiceData = null;
      let saleRow = null;
      let rowNumber = null;
      let existingInvoice = null;

      // Fetch real data based on input
      if (invoiceParams.invoiceNumber) {
        console.log('🎯 FRONTEND: Taking INVOICE NUMBER path');
        console.log('🎯 FRONTEND: Processing invoice number generation:', invoiceParams.invoiceNumber);
        // Parse invoice number to find matching sale
        const prefix = templateData.invoicePrefix || 'INV';
        const invoiceNum = invoiceParams.invoiceNumber.trim().toUpperCase();

        // Validate invoice number format
        if (!invoiceNum || invoiceNum.length <= prefix.length) {
          setError('Please enter a valid invoice number.');
          return;
        }

        // Check if invoice number starts with the correct prefix
        if (!invoiceNum.startsWith(prefix)) {
          setError(`Invoice number must start with "${prefix}". Please check your invoice prefix in settings.`);
          return;
        }

        // Extract numeric part from invoice number
        const numericPart = invoiceNum.substring(prefix.length);
        if (!numericPart || !/^\d+$/.test(numericPart)) {
          setError(`Invalid invoice number format. Expected format: ${prefix}001, ${prefix}002, etc.`);
          return;
        }

        const invoiceNumber = parseInt(numericPart);
        if (invoiceNumber <= 0) {
          setError('Invoice number must be greater than 0.');
          return;
        }

        // Find sale by invoice number - prioritize exact matches first
        let matchingSale = null;

        // First, try to find by exact invoice_no match (for already generated invoices)
        matchingSale = sales.find(sale => sale.invoice_no === invoiceNum);

        // If not found, use ID-based lookup (invoice number should match sale ID)
        // Invoice number "005" should find the sale with ID = 5
        if (!matchingSale) {
          matchingSale = sales.find(sale => parseInt(sale.id) === invoiceNumber);
          if (matchingSale) {
            console.log(`📊 Found sale by ID lookup: invoice ${invoiceNum} -> sale with ID ${invoiceNumber} (sale.id: ${matchingSale.id}, invoice_no: ${matchingSale.invoice_no}, customer: ${matchingSale.customer_name})`);
          } else {
            console.log(`❌ No sale found with ID ${invoiceNumber}. Available sale IDs:`, sales.map(s => s.id));
          }
        }

        if (!matchingSale) {
          console.log('❌ FRONTEND: No matching sale found for invoice number generation:', invoiceNum);
          console.log('❌ FRONTEND: Searched sales:', sales.length, 'sales');
          setError(`Invoice ${invoiceNum} not found. Please check the invoice number or refresh the sales data.`);
          return;
        }

        // Check if invoice is already generated (must have existing_invoice data)
        if (matchingSale.invoice_generated && matchingSale.existing_invoice) {
          console.log('📄 Invoice already generated, loading existing invoice');
          console.log('📄 Existing invoice data:', matchingSale.existing_invoice);
          existingInvoice = {
            invoice_no: matchingSale.existing_invoice.invoice_no,
            pdf_path: matchingSale.existing_invoice.pdf_path,
            whatsapp_link: matchingSale.existing_invoice.whatsapp_link,
            saleRow: matchingSale
          };
        } else {
          // Use the sale ID as row number for invoice ID generation (invoice number matches sale ID)
          rowNumber = matchingSale.id;
          saleRow = matchingSale;
          console.log('📊 Found sale data for new invoice:', invoiceNum, 'Sale ID:', saleRow.id, 'Row Number:', rowNumber);
        }

      } else if (invoiceParams.customerName) {
        // Correct approach: Find customer by name, then find their sales by customer_id
        console.log(`🔍 FRONTEND: Taking CUSTOMER NAME path`);
        console.log(`🔍 FRONTEND: Processing customer name generation: "${invoiceParams.customerName}"`);

        try {
          // Get customer details by name
          const customerResponse = await backendApi.getCustomers();
          const customers = customerResponse.customers || customerResponse || [];
          const searchCustomerName = invoiceParams.customerName.trim().toLowerCase();

          const matchingCustomer = customers.find(customer =>
            customer.name && customer.name.trim().toLowerCase() === searchCustomerName
          );

          if (!matchingCustomer) {
            setError(`Customer "${invoiceParams.customerName}" not found. Available customers: ${customers.slice(0, 5).map(c => c.name).join(', ')}`);
            return;
          }

          console.log(`✅ Found customer:`, matchingCustomer);

          // Now find sales for this customer using their ID
          const customerSales = sales.filter(sale =>
            sale.customer_id && sale.customer_id.toString() === matchingCustomer.id.toString()
          );

          console.log(`📊 Found ${customerSales.length} sales for customer ${matchingCustomer.name} (ID: ${matchingCustomer.id})`);

          if (customerSales.length === 0) {
            setError(`Customer "${invoiceParams.customerName}" exists but has no sales records. Please create a sale first.`);
            return;
          }

          if (customerSales.length === 1) {
            // Single sale - check if already generated
            const sale = customerSales[0];
            console.log('📄 FRONTEND: Customer has single sale, checking if already generated:', sale.invoice_generated);
            if (sale.invoice_generated) {
              console.log('📄 Customer invoice already generated, loading existing invoice');
              existingInvoice = {
                invoice_no: sale.invoice_no,
                pdf_path: sale.pdf_path,
                whatsapp_link: sale.whatsapp_link,
                saleRow: sale
              };
            } else {
              // Generate new invoice
              saleRow = sale;
              rowNumber = sales.indexOf(saleRow) + 1;
              console.log('📊 FRONTEND: Generating new invoice for customer:', invoiceParams.customerName, 'rowNumber:', rowNumber);
            }
          } else {
            // Multiple sales - show selection dialog
            const invoiceSummaries = await Promise.all(
              customerSales.map(async (sale, index) => {
                const rowNumber = sales.indexOf(sale) + 1;
                const invoiceData = await prepareRealInvoiceData(sale, rowNumber);
                return {
                  id: `INV-${Date.now()}-${index}`,
                  rowNumber,
                  saleRow: sale,
                  invoiceData,
                  summary: {
                    invoiceId: sale.invoice_no || invoiceData.invoiceId,
                    date: invoiceData.date,
                    totalPurchase: invoiceData.totalAmount || 0,
                    totalPaid: invoiceData.totalPaid || 0,
                    totalDue: invoiceData.dueAmount || 0,
                    itemsCount: invoiceData.items?.length || 0,
                    alreadyGenerated: sale.invoice_generated || false
                  }
                };
              })
            );

            setCustomerInvoices(invoiceSummaries);
            setSelectedCustomerName(invoiceParams.customerName);
            setShowCustomerInvoiceSelector(true);
            return; // Don't continue with generation
          }
        } catch (error) {
          console.error('Error finding customer:', error);
          setError('Failed to search for customer. Please try again.');
          return;
        }

      } else {
        setError('Please enter either an invoice number or customer name.');
        return;
      }

      // Handle existing invoice (already generated)
      if (existingInvoice) {
        console.log('📄 Loading existing invoice:', existingInvoice);

        // Prepare invoice data for display
        const invoiceData = await prepareRealInvoiceData(existingInvoice.saleRow, existingInvoice.saleRow.id);

        // Override the generated invoiceId with the existing invoice number
        const correctedInvoiceData = {
          ...invoiceData,
          invoiceId: existingInvoice.invoice_no
        };

        // Generate HTML for display
        const previewResponse = await backendApi.generateInvoicePreview({
          invoiceData: correctedInvoiceData,
          businessData: businessData,
          templateData: templateData
        });

        if (previewResponse.success && previewResponse.html) {
          console.log('✅ FRONTEND: Setting generated invoice for existing invoice (invoice number path)');
          console.log('✅ FRONTEND: correctedInvoiceData:', correctedInvoiceData);
          console.log('✅ FRONTEND: existingInvoice:', existingInvoice);
          setGeneratedInvoice({
            ...correctedInvoiceData,
            id: existingInvoice.saleRow.id, // Add the sales invoice ID for PDF downloads
            invoiceId: existingInvoice.invoice_no,
            htmlTemplate: previewResponse.html,
            pdfPath: existingInvoice.pdf_path,
            whatsappLink: existingInvoice.whatsapp_link
          });
          setShowInvoice(true);
          setSuccess('Existing invoice loaded successfully!');
          setTimeout(() => setSuccess(null), 3000);
        } else {
          throw new Error('Failed to load existing invoice preview');
        }

        setInvoiceParams({ invoiceNumber: '', customerName: '' });
        setInvoiceNumberInput('');
        return;
      }

      // Generate new invoice
      if (!saleRow) {
        setError('Failed to find sale data. Please try again.');
        return;
      }

      // Prepare real invoice data from sale row
      realInvoiceData = await prepareRealInvoiceData(saleRow, rowNumber);

      console.log('🎨 Generating new invoice with real data:', realInvoiceData);

      // Generate HTML template for preview using backend API
      const previewResponse = await backendApi.generateInvoicePreview({
        invoiceData: realInvoiceData,
        businessData: businessData,
        templateData: templateData
      });

      if (previewResponse.success && previewResponse.html) {
        // Update the preview with the generated invoice
        setPreviewInvoice({
          htmlTemplate: previewResponse.html,
          template: templateData.theme || 'simple',
          invoiceData: realInvoiceData
        });
        setShowPreview(true);
      } else {
        throw new Error('Failed to generate invoice preview');
      }

      // Also call backend for PDF generation and WhatsApp link
      const invoiceData = {
        rowNumber: rowNumber,
        saleRow: saleRow,
        customerName: invoiceParams.customerName || null,
        templateData: templateData,
        selectedTemplate: templateData.theme || 'simple'
      };

      console.log('📤 Calling backend for PDF generation:', invoiceData);

      const result = await backendApi.generateInvoice(invoiceData);
      console.log('✅ Backend PDF generation response:', result);

      // Store the generated invoice for display (with PDF and WhatsApp links)
      if (result.invoice) {
        const newGeneratedInvoice = {
          ...result.invoice,
          htmlTemplate: result.invoice.htmlTemplate || '' // Use HTML from backend generateInvoice response
        };
        setGeneratedInvoice(newGeneratedInvoice);
        setShowInvoice(true);
        // The invoice will now show directly in the preview area instead of a popup
      }

      setSuccess('Invoice generated successfully!');
      setInvoiceParams({ invoiceNumber: '', customerName: '' });
      setInvoiceNumberInput(''); // Clear the local input state
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error generating invoice:', err);

      // Provide more specific error messages based on error type
      let errorMessage = 'Failed to generate invoice. Please try again.';

      if (err.message?.includes('not found')) {
        errorMessage = 'Sale data not found. Please refresh the page and try again.';
      } else if (err.message?.includes('network') || err.message?.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.message?.includes('template')) {
        errorMessage = 'Template error. Please check your invoice settings.';
      } else if (err.message?.includes('PDF')) {
        errorMessage = 'PDF generation failed. Please try again.';
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }

      setError(errorMessage);
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Helper function to generate customer summary HTML
  const generateCustomerSummaryHTML = (customerInvoices, customerName) => {
    const totalPurchase = customerInvoices.reduce((sum, inv) => sum + inv.summary.totalPurchase, 0);
    const totalPaid = customerInvoices.reduce((sum, inv) => sum + inv.summary.totalPaid, 0);
    const totalDue = customerInvoices.reduce((sum, inv) => sum + inv.summary.totalDue, 0);
    const totalItems = customerInvoices.reduce((sum, inv) => sum + inv.summary.itemsCount, 0);

    return `
      <div style="font-family: 'Arial', sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #1976d2;">
          <h1 style="margin: 0; color: #1976d2; font-size: 28px;">Customer Summary</h1>
          <h2 style="margin: 10px 0; color: #333; font-size: 20px;">${customerName}</h2>
          <p style="margin: 5px 0; color: #666;">Generated on ${new Date().toLocaleDateString('en-IN')}</p>
        </div>

        <table style="width: 100%; border-collapse: collapse; margin: 20px 0; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <thead>
            <tr style="background: linear-gradient(135deg, #1976d2, #42a5f5); color: white;">
              <th style="padding: 15px; text-align: left; font-weight: 600;">Invoice ID</th>
              <th style="padding: 15px; text-align: left; font-weight: 600;">Date</th>
              <th style="padding: 15px; text-align: right; font-weight: 600;">Total Purchase</th>
              <th style="padding: 15px; text-align: right; font-weight: 600;">Amount Paid</th>
              <th style="padding: 15px; text-align: right; font-weight: 600;">Amount Due</th>
              <th style="padding: 15px; text-align: center; font-weight: 600;">Items</th>
            </tr>
          </thead>
          <tbody>
            ${customerInvoices.map((invoice, index) => `
              <tr style="background: ${index % 2 === 0 ? '#f8f9fa' : 'white'};">
                <td style="padding: 12px 15px; border-bottom: 1px solid #ddd;">${invoice.summary.invoiceId}</td>
                <td style="padding: 12px 15px; border-bottom: 1px solid #ddd;">${invoice.summary.date}</td>
                <td style="padding: 12px 15px; border-bottom: 1px solid #ddd; text-align: right;">₹${invoice.summary.totalPurchase.toLocaleString('en-IN')}</td>
                <td style="padding: 12px 15px; border-bottom: 1px solid #ddd; text-align: right; color: #28a745;">₹${invoice.summary.totalPaid.toLocaleString('en-IN')}</td>
                <td style="padding: 12px 15px; border-bottom: 1px solid #ddd; text-align: right; color: ${invoice.summary.totalDue > 0 ? '#dc3545' : '#28a745'}; font-weight: bold;">₹${invoice.summary.totalDue.toLocaleString('en-IN')}</td>
                <td style="padding: 12px 15px; border-bottom: 1px solid #ddd; text-align: center;">${invoice.summary.itemsCount}</td>
              </tr>
            `).join('')}
          </tbody>
          <tfoot>
            <tr style="background: #e8f4fd; border-top: 2px solid #1976d2;">
              <td colspan="2" style="padding: 15px; font-weight: bold; font-size: 16px;">TOTAL SUMMARY</td>
              <td style="padding: 15px; text-align: right; font-weight: bold; font-size: 16px; color: #1976d2;">₹${totalPurchase.toLocaleString('en-IN')}</td>
              <td style="padding: 15px; text-align: right; font-weight: bold; font-size: 16px; color: #28a745;">₹${totalPaid.toLocaleString('en-IN')}</td>
              <td style="padding: 15px; text-align: right; font-weight: bold; font-size: 16px; color: ${totalDue > 0 ? '#dc3545' : '#28a745'};">₹${totalDue.toLocaleString('en-IN')}</td>
              <td style="padding: 15px; text-align: center; font-weight: bold; font-size: 16px;">${totalItems}</td>
            </tr>
          </tfoot>
        </table>

        <div style="margin-top: 30px; padding: 20px; background: #f8f9fa; border-radius: 8px;">
          <h3 style="margin: 0 0 15px 0; color: #333;">Summary Details:</h3>
          <div style="display: flex; justify-content: space-between; flex-wrap: wrap; gap: 20px;">
            <div style="flex: 1; min-width: 200px;">
              <p style="margin: 5px 0;"><strong>Total Invoices:</strong> ${customerInvoices.length}</p>
              <p style="margin: 5px 0;"><strong>Total Items Purchased:</strong> ${totalItems}</p>
            </div>
            <div style="flex: 1; min-width: 200px;">
              <p style="margin: 5px 0;"><strong>Payment Status:</strong>
                <span style="color: ${totalDue === 0 ? '#28a745' : '#dc3545'}; font-weight: bold;">
                  ${totalDue === 0 ? 'Fully Paid' : 'Outstanding Balance'}
                </span>
              </p>
              ${totalDue > 0 ? `<p style="margin: 5px 0;"><strong>Outstanding Amount:</strong> <span style="color: #dc3545; font-weight: bold;">₹${totalDue.toLocaleString('en-IN')}</span></p>` : ''}
            </div>
          </div>
        </div>

        <div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 2px solid #e0e0e0; color: #666;">
          <p style="margin: 10px 0; font-size: 16px; font-weight: 500;">Thank you for your business!</p>
          <p style="margin: 5px 0; font-size: 14px;">Generated by Dhandhapaani</p>
        </div>
      </div>
    `;
  };

  // Helper function to prepare real invoice data from sale row
  const prepareRealInvoiceData = async (saleRow, rowNumber) => {
    try {
      // Database format (sales_invoices with sales_items)
      const items = saleRow.items.map(item => ({
        name: item.name || item.item_name,
        quantity: parseFloat(item.quantity) || 0,
        price: parseFloat(item.unit_price || item.price) || 0,
        mrp: parseFloat(item.mrp_price || item.mrp) || 0,
        mrp_price: parseFloat(item.mrp_price || item.mrp) || 0,
        discount: parseFloat(item.discount) || 0,
        total: parseFloat(item.line_total || item.total) || 0
      }));

      const totalAmount = parseFloat(saleRow.total_amount || saleRow['Total Amount']) || 0;
      const finalAmount = parseFloat(saleRow.total_amount || saleRow['Total Amount']) || 0; // Assuming no discount in simple case
      const totalPaid = parseFloat(saleRow.total_paid || saleRow['Total Paid']) || 0;
      const dueAmount = parseFloat(saleRow.due_amount || saleRow['Due Amount']) || 0;
      const customerName = saleRow['Customer Name'] || saleRow.customer_name || 'Walk-in Customer';
      const phone = saleRow['Phone Number'] || saleRow.customer_phone || '';
      const paymentMode = saleRow['Payment Mode'] || saleRow.payment_mode || 'Cash';

      // Calculate items subtotal (from parsed items)
      const itemsSubtotal = items.reduce((sum, item) => sum + item.total, 0);

      // Get discount from database
      const discount = parseFloat(saleRow.total_discount) || 0;

      // Calculate tax amount based on template settings
      const taxRate = parseFloat(templateData.taxRate || 0);
      const taxAmount = (finalAmount * taxRate) / 100;

      // Final amount after tax
      const finalAmountWithTax = finalAmount + taxAmount;

      // Generate professional invoice ID with configurable prefix
      const prefix = templateData.invoicePrefix || 'INV';
      const paddedRowNumber = rowNumber.toString().padStart(3, '0');
      const invoiceId = `${prefix}${paddedRowNumber}`;

      return {
        invoiceId: invoiceId,
        invoice_no: invoiceId, // For template compatibility
        date: new Date().toLocaleDateString('en-IN'),
        customerName: customerName,
        customer_name: customerName, // For template compatibility
        phone: phone,
        customer_phone: phone, // For template compatibility
        items: items,
        // Financial breakdown
        itemsSubtotal: itemsSubtotal,
        totalAmount: totalAmount,
        total_amount: totalAmount, // For template compatibility
        discount: discount,
        discountedAmount: finalAmount,
        taxRate: taxRate,
        taxAmount: taxAmount,
        finalAmountWithTax: finalAmountWithTax,
        totalPaid: totalPaid,
        total_paid: totalPaid, // For template compatibility
        dueAmount: dueAmount,
        due_amount: dueAmount, // For template compatibility
        paymentMode: paymentMode,
        rowNumber: rowNumber,
        // Custom fields
        salesNote: templateData.salesNote || '',
        authorizedSignature: templateData.authorizedSignature || '',
        // Payment terms (configurable)
        paymentTerms: templateData.paymentTerms || 'Net 30'
      };
    } catch (error) {
      console.error('Error preparing real invoice data:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="textSecondary">
          {loadingMessage}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Please wait while we load your invoice data...
        </Typography>
      </Box>
    );
  }

  const selectedInvoiceKey = getInvoiceKey(selectedInvoice);
  const selectedInvoiceTotal = Number(selectedInvoice?.total_amount ?? selectedInvoice?.totalAmount ?? 0);
  const selectedInvoicePaid = Number(selectedInvoice?.total_paid ?? selectedInvoice?.totalPaid ?? 0);
  const selectedInvoiceDueRaw = selectedInvoice?.due_amount ?? selectedInvoice?.dueAmount;
  const selectedInvoiceComputedDue = Math.max(selectedInvoiceTotal - selectedInvoicePaid, 0);
  let selectedInvoiceDue = Number.isFinite(Number(selectedInvoiceDueRaw))
    ? Math.max(Number(selectedInvoiceDueRaw), 0)
    : selectedInvoiceComputedDue;

  // Guard against inconsistent due values (e.g. due=0 while unpaid and total is pending).
  const selectedInvoiceStatus = getEffectiveInvoiceStatus(selectedInvoice);
  if (
    selectedInvoiceDue === 0 &&
    selectedInvoiceTotal > selectedInvoicePaid &&
    (selectedInvoiceStatus === 'unpaid' || selectedInvoiceStatus === 'partial' || selectedInvoiceStatus === 'overdue')
  ) {
    selectedInvoiceDue = selectedInvoiceComputedDue;
  }
  const selectedInvoiceIssueDate = selectedInvoice?.invoice_date || selectedInvoice?.date || null;
  const selectedInvoiceDueDate = selectedInvoice
    ? (
      selectedInvoice.due_date ||
      selectedInvoice.dueDate ||
      calculateDueDateIN(
        selectedInvoiceIssueDate,
        selectedInvoice.payment_terms || selectedInvoice.paymentTerms || templateData.paymentTerms
      )
    )
    : null;
  const historyStatusCards = [
    {
      key: 'paid',
      label: 'Paid Invoices',
      count: invoiceHistoryStats.paid.count,
      amount: invoiceHistoryStats.paid.amount,
      accent: '#16a34a',
      bg: 'linear-gradient(145deg, rgba(22, 163, 74, 0.12), rgba(255, 255, 255, 0.95))'
    },
    {
      key: 'partial',
      label: 'Partial Paid',
      count: invoiceHistoryStats.partial.count,
      amount: invoiceHistoryStats.partial.amount,
      accent: '#2563eb',
      bg: 'linear-gradient(145deg, rgba(37, 99, 235, 0.12), rgba(255, 255, 255, 0.95))'
    },
    {
      key: 'unpaid',
      label: 'Unpaid Invoices',
      count: invoiceHistoryStats.unpaid.count,
      amount: invoiceHistoryStats.unpaid.amount,
      accent: '#ca8a04',
      bg: 'linear-gradient(145deg, rgba(202, 138, 4, 0.12), rgba(255, 255, 255, 0.95))'
    },
    {
      key: 'remaining',
      label: 'Remaining Payment',
      count: invoiceHistoryStats.remaining.count,
      amount: invoiceHistoryStats.remaining.amount,
      accent: '#0f766e',
      bg: 'linear-gradient(145deg, rgba(15, 118, 110, 0.12), rgba(255, 255, 255, 0.95))'
    },
    {
      key: 'overdue',
      label: 'Overdue Invoices',
      count: invoiceHistoryStats.overdue.count,
      amount: invoiceHistoryStats.overdue.amount,
      accent: '#dc2626',
      bg: 'linear-gradient(145deg, rgba(220, 38, 38, 0.12), rgba(255, 255, 255, 0.95))'
    }
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Invoice Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Single-template invoice workflow with quick create and clean history view.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {infoMessage && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {infoMessage}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(event, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 42,
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              background: 'linear-gradient(90deg, #2563eb 0%, #0ea5e9 100%)'
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 42,
              fontWeight: 700,
              color: '#475569',
              '&.Mui-selected': {
                color: '#1d4ed8'
              }
            }
          }}
        >
          <Tab value="create" label="Create Invoice" />
          <Tab value="history" label="Invoice History" />
        </Tabs>
      </Box>

      {activeTab === 'create' && (
      <>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.75,
              borderRadius: 2.5,
              borderColor: '#bfdbfe',
              background: 'linear-gradient(160deg, rgba(219, 234, 254, 0.7) 0%, rgba(255, 255, 255, 0.98) 100%)'
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>
              Invoice Prefix
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.4, fontWeight: 800, color: '#1d4ed8' }}>
              {templateData.invoicePrefix || 'INV'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.75,
              borderRadius: 2.5,
              borderColor: '#bbf7d0',
              background: 'linear-gradient(160deg, rgba(220, 252, 231, 0.75) 0%, rgba(255, 255, 255, 0.98) 100%)'
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>
              Payment Terms
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.4, fontWeight: 800, color: '#047857' }}>
              {templateData.paymentTerms || 'Net 30'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.75,
              borderRadius: 2.5,
              borderColor: '#fecaca',
              background: 'linear-gradient(160deg, rgba(254, 226, 226, 0.75) 0%, rgba(255, 255, 255, 0.98) 100%)'
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>
              Tax Rate
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.4, fontWeight: 800, color: '#b91c1c' }}>
              {Number(templateData.taxRate || 0)}%
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Main Content Area - Responsive Layout */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.7fr) minmax(320px, 1fr)' },
        gap: 3,
        mb: 4
      }}>
        {/* Left Side - Enhanced Invoice Preview/Display */}
        <Box sx={{
          minWidth: 0
        }}>
          <Card sx={{
            minHeight: { xs: 360, md: 440 },
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '24px',
            border: '1px solid rgba(37, 99, 235, 0.14)',
            boxShadow: '0 20px 44px rgba(15, 23, 42, 0.09)',
            background: 'radial-gradient(circle at top right, rgba(186, 230, 253, 0.45), transparent 48%), linear-gradient(175deg, #f8fafc 0%, #eff6ff 55%, #f0fdf4 100%)',
            position: 'relative'
          }}>
            {/* Floating action buttons */}
            <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
              <IconButton
                sx={{
                  width: 38,
                  height: 38,
                  background: 'rgba(255,255,255,0.95)',
                  border: '1px solid rgba(148, 163, 184, 0.35)',
                  boxShadow: '0 6px 16px rgba(15, 23, 42, 0.15)',
                  '&:hover': { background: 'white', transform: 'translateY(-1px)' },
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setFullScreenPreview(!fullScreenPreview)}
              >
                <FullscreenIcon />
              </IconButton>
            </Box>

            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
              <Box sx={{
                p: { xs: 2, md: 2.5 },
                pb: { xs: 1.6, md: 2 },
                background: showInvoice ?
                  'linear-gradient(130deg, #047857 0%, #0f766e 65%, #0ea5a4 100%)' :
                  'linear-gradient(130deg, #0f172a 0%, #1e3a8a 55%, #0ea5e9 100%)',
                color: 'white'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    background: 'rgba(255,255,255,0.18)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <ReceiptIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.35 }}>
                      {showInvoice ? 'Invoice Ready to Share' : 'Live Invoice Preview'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.88 }}>
                      {showInvoice
                        ? 'Use quick actions below for download, print, and WhatsApp.'
                        : 'Compact preview with key details. No long empty canvas.'
                      }
                    </Typography>
                  </Box>
                  {showInvoice && (
                    <Box sx={{ ml: 'auto' }}>
                      <Chip
                        label="READY"
                        sx={{
                          background: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Enhanced preview area */}
              <Box sx={{ p: { xs: 1.25, md: 1.6 }, background: 'transparent' }}>
                <Box
                  sx={{
                    border: '1px solid #cbd5e1',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.9), 0 10px 24px rgba(15, 23, 42, 0.06)',
                    position: 'relative',
                    minHeight: 0
                  }}
                >
                  <Box sx={{ p: { xs: 1.1, md: 1.35 } }}>
                    {(() => {
                      const previewData = showInvoice
                        ? (generatedInvoice?.invoiceData || generatedInvoice || previewInvoice?.invoiceData || null)
                        : (previewInvoice?.invoiceData || previewInvoice || null);

                      if (!previewData) {
                        return (
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 250,
                            flexDirection: 'column',
                            textAlign: 'center'
                          }}>
                            <Box sx={{
                              width: 64,
                              height: 64,
                              background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                              borderRadius: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 1.5
                            }}>
                              <ReceiptIcon sx={{ fontSize: 30, color: '#475569' }} />
                            </Box>
                            <Typography variant="subtitle1" gutterBottom sx={{ color: '#0f172a', fontWeight: 700 }}>
                              Preview is ready when you are
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, maxWidth: 340 }}>
                              Use `Load Preview` or generate directly from a sale. This panel stays compact to avoid blank space.
                            </Typography>
                            <Button size="small" variant="outlined" onClick={() => generateTemplatePreview()}>
                              Load Preview
                            </Button>
                          </Box>
                        );
                      }

                      const items = (() => {
                        if (Array.isArray(previewData.items)) return previewData.items;
                        if (typeof previewData.items === 'string') {
                          try {
                            const parsed = JSON.parse(previewData.items);
                            return Array.isArray(parsed) ? parsed : [];
                          } catch {
                            return [];
                          }
                        }
                        return [];
                      })();
                      const issueDate = previewData.date || previewData.invoiceDate || previewData.invoice_date || new Date().toLocaleDateString('en-IN');
                      const paymentTerms = previewData.paymentTerms || previewData.payment_terms || templateData.paymentTerms || 'Net 30';
                      const dueDate = previewData.dueDate || previewData.due_date || calculateDueDateIN(issueDate, paymentTerms);
                      const customerName = previewData.customerName || previewData.customer_name || 'Walk-in Customer';
                      const customerPhone = previewData.phone || previewData.customerPhone || previewData.customer_phone || '--';
                      const subtotal = Number(previewData.totalAmount ?? previewData.total_amount ?? previewData.subtotal ?? 0);
                      const totalPaid = Number(previewData.totalPaid ?? previewData.total_paid ?? 0);
                      const dueAmountRaw = previewData.dueAmount ?? previewData.due_amount;
                      const dueAmount = Number.isFinite(Number(dueAmountRaw))
                        ? Math.max(Number(dueAmountRaw), 0)
                        : Math.max(0, subtotal - totalPaid);
                      const taxRate = Number(previewData.taxRate || previewData.tax_rate || templateData.taxRate || 0);
                      const taxAmount = Number(previewData.taxAmount || previewData.tax_amount || 0);
                      const effectiveStatus = getEffectiveInvoiceStatus(previewData);
                      const status = effectiveStatus === 'paid' ? 'Paid' : effectiveStatus === 'partial' ? 'Partial' : effectiveStatus === 'overdue' ? 'Overdue' : 'Unpaid';
                      const invoiceId = previewData.invoiceId || previewData.invoiceNo || previewData.invoice_no || '--';
                      const billFromName = previewData.business_name || businessData.companyName || businessData.name || 'Your Business Name';
                      const billFromAddress = previewData.business_address || businessData.address || 'Business Address';
                      const billFromPhone = previewData.business_phone || businessData.phone || businessData.businessPhone || '--';

                      return (
                        <Box sx={{
                          background: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          p: { xs: 1.5, md: 2 }
                        }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                            <Box>
                              <Typography variant="overline" sx={{ color: 'text.secondary' }}>Invoice</Typography>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{invoiceId}</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Chip
                                size="small"
                                label={status}
                                color={status === 'Paid' ? 'success' : status === 'Partial' ? 'warning' : 'default'}
                                sx={{ mb: 0.5 }}
                              />
                              <Typography variant="caption" display="block" color="text.secondary">
                                Issue: {formatDateIN(issueDate)}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                Due: {formatDateIN(dueDate)}
                              </Typography>
                            </Box>
                          </Box>

                          <Divider sx={{ my: 1.5 }} />

                          <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Bill From</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{billFromName}</Typography>
                              <Typography variant="caption" display="block">{billFromAddress}</Typography>
                              <Typography variant="caption" display="block">{billFromPhone}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Bill To</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{customerName}</Typography>
                              <Typography variant="caption" display="block">{customerPhone}</Typography>
                              <Typography variant="caption" display="block">{paymentTerms}</Typography>
                            </Grid>
                          </Grid>

                          <Divider sx={{ my: 1.5 }} />

                          <Typography variant="caption" color="text.secondary">Package Summary</Typography>
                          <Table size="small" sx={{ mt: 0.5 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ py: 0.75 }}>Description</TableCell>
                                <TableCell sx={{ py: 0.75 }} align="right">Qty</TableCell>
                                <TableCell sx={{ py: 0.75 }} align="right">Price</TableCell>
                                <TableCell sx={{ py: 0.75 }} align="right">Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {items.slice(0, 4).map((item, idx) => {
                                const name = item.name || item.item_name || `Item ${idx + 1}`;
                                const qty = Number(item.quantity || 0);
                                const price = Number(item.price || item.unit_price || 0);
                                const amount = Number(item.total ?? item.amount ?? item.line_total ?? qty * price);
                                return (
                                  <TableRow key={`${name}-${idx}`}>
                                    <TableCell sx={{ py: 0.6 }}>{name}</TableCell>
                                    <TableCell sx={{ py: 0.6 }} align="right">{qty}</TableCell>
                                    <TableCell sx={{ py: 0.6 }} align="right">{formatINR(price)}</TableCell>
                                    <TableCell sx={{ py: 0.6 }} align="right">{formatINR(amount)}</TableCell>
                                  </TableRow>
                                );
                              })}
                              {items.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={4} sx={{ py: 1.5, color: 'text.secondary' }}>
                                    No line items yet
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>

                          <Box sx={{ mt: 1, ml: 'auto', maxWidth: 260 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption">Sub Total</Typography>
                              <Typography variant="caption">{formatINR(subtotal)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption">Tax ({taxRate}%)</Typography>
                              <Typography variant="caption">{formatINR(taxAmount)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption">Paid</Typography>
                              <Typography variant="caption">{formatINR(totalPaid)}</Typography>
                            </Box>
                            <Divider sx={{ my: 0.5 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>Due</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatINR(dueAmount)}</Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })()}
                  </Box>
                </Box>
              </Box>

              {/* Enhanced Action Buttons - Only show when invoice is generated */}
              {showInvoice && generatedInvoice && (
                <Box sx={{
                  p: { xs: 1.25, md: 1.5 },
                  background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(255, 255, 255, 0.98) 100%)',
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-start'
                }}>
                  <Button
                    onClick={async () => {
                      console.log('🔍 Download PDF - generatedInvoice:', generatedInvoice);
                      console.log('🔍 HTML Template length:', generatedInvoice?.htmlTemplate?.length);
                      console.log('🔍 html2pdf available:', typeof html2pdf);
                      const printProfile = resolveInvoicePrintProfile(templateData);

                      if (generatedInvoice?.id) {
                        try {
                          console.log('📄 Downloading PDF via backend API');

                          const backendUrl = process.env.REACT_APP_BACKEND_API_URL || 'https://dhandhapaani.store';

                          // Get clean token without Bearer prefix
                          let authToken = localStorage.getItem('authToken') || localStorage.getItem('accessToken') || '';
                          console.log('🔑 Raw authToken from localStorage:', authToken ? authToken.substring(0, 50) + '...' : 'none');

                          if (authToken.startsWith('Bearer ')) {
                            authToken = authToken.replace('Bearer ', '');
                            console.log('🔑 Stripped Bearer prefix, token now:', authToken.substring(0, 50) + '...');
                          }

                          console.log('🔑 Final Authorization header will be: Bearer', authToken.substring(0, 50) + '...');

                          // Use the new download endpoint
                          const response = await fetch(`${backendUrl}/api/invoices/${generatedInvoice.id}/download-pdf`, {
                            method: 'GET',
                            headers: {
                              'Authorization': `Bearer ${authToken}`,
                            }
                          });

                          if (response.ok) {
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            const filename = `Invoice-${generatedInvoice.invoiceId || generatedInvoice.id}.pdf`;
                            a.download = filename;
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                            console.log(`📥 PDF downloaded: ${filename} for invoice ${generatedInvoice.id}`);
                            setSuccess(`PDF downloaded: ${filename}`);
                            setTimeout(() => setSuccess(null), 3000);
                            return;
                          } else {
                            console.error('PDF download failed:', response.status, response.statusText);
                            setError('Failed to download PDF. Please try again.');
                          }

                          // 3) Final fallback: client-side html2pdf screenshot
                          console.warn('⚠️ Render/download fallback to html2pdf.js');
                          const opt = getInvoiceHtml2PdfOptions(
                            printProfile,
                            `${generatedInvoice.invoiceId}.pdf`
                          );
                          const result = await html2pdf().set(opt).from(generatedInvoice.htmlTemplate).save();
                          console.log('✅ html2pdf.js screenshot fallback completed:', result);
                          setSuccess('PDF downloaded successfully!');
                          setTimeout(() => setSuccess(null), 2000);

                        } catch (error) {
                          console.error('❌ Error with backend PDF generation:', error);
                          console.error('❌ Error details:', error.message, error.stack);

                          // Final fallback to html2pdf.js screenshot
                          console.log('🔄 Final fallback to html2pdf.js screenshot');
                          try {
                            const opt = getInvoiceHtml2PdfOptions(
                              printProfile,
                              `${generatedInvoice.invoiceId}.pdf`
                            );

                            const result = await html2pdf().set(opt).from(generatedInvoice.htmlTemplate).save();
                            console.log('✅ Final html2pdf.js fallback completed:', result);
                            setSuccess('PDF downloaded successfully!');
                            setTimeout(() => setSuccess(null), 2000);
                          } catch (fallbackError) {
                            console.error('❌ All PDF generation methods failed:', fallbackError);
                            setError('Failed to generate PDF. Please try again.');
                          }
                        }
                      } else {
                        console.error('❌ No HTML template found in generatedInvoice');
                        console.log('❌ generatedInvoice keys:', Object.keys(generatedInvoice || {}));
                        setError('No invoice template found. Please generate the invoice first.');
                      }
                    }}
                    variant="contained"
                    startIcon={<PictureAsPdfIcon />}
                    size="medium"
                    sx={{
                      px: 2.2,
                      py: 1,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      boxShadow: '0 8px 20px rgba(220, 38, 38, 0.25)',
                      fontSize: '0.92rem',
                      fontWeight: 700,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 12px 28px rgba(220, 38, 38, 0.32)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Download PDF
                  </Button>

                  {generatedInvoice.phone && (
                    <Button
                      onClick={() => {
                        (async () => {
                          try {
                            // Ensure phone is a string before using string methods
                            const phoneValue = generatedInvoice.phone;
                            const phone = (phoneValue && typeof phoneValue === 'string')
                              ? phoneValue.replace(/\D/g, '')
                              : (phoneValue ? String(phoneValue).replace(/\D/g, '') : '');
                            let businessName = 'Your Business Name';
                            if (generatedInvoice.business_name) {
                              businessName = generatedInvoice.business_name;
                            } else if (generatedInvoice.company_name) {
                              businessName = generatedInvoice.company_name;
                            } else if (businessData.companyName) {
                              businessName = businessData.companyName;
                            } else if (businessData.name) {
                              businessName = businessData.name;
                            }

                            const apiMessage = `INVOICE DETAILS\n\n` +
                              `Invoice ID: ${generatedInvoice.invoiceId}\n` +
                              `Customer: ${generatedInvoice.customerName}\n` +
                              `Date: ${generatedInvoice.date}\n` +
                              `Total Amount: Rs.${generatedInvoice.subtotal?.toFixed(2) || '0.00'}\n` +
                              `Amount Paid: Rs.${generatedInvoice.totalPaid?.toFixed(2) || '0.00'}\n` +
                              `Amount Due: Rs.${generatedInvoice.dueAmount?.toFixed(2) || '0.00'}\n` +
                              `Payment Terms: ${generatedInvoice.paymentTerms || 'Net 30'}`;

                            if (generatedInvoice?.id) {
                              try {
                                await backendApi.sendInvoicePdfToCustomer(generatedInvoice.id, {
                                  caption: `Invoice ${generatedInvoice.invoiceId || generatedInvoice.id}`,
                                  footer: businessName,
                                  full: 1
                                });
                                setSuccess('Invoice PDF sent on WhatsApp successfully.');
                                setTimeout(() => setSuccess(null), 3000);
                                return;
                              } catch (apiError) {
                                console.warn('Direct WhatsApp PDF send failed, trying text API:', apiError);
                              }
                            }

                            if (phone) {
                              try {
                                await backendApi.sendWhatsAppText({
                                  number: phone,
                                  message: apiMessage,
                                  footer: businessName,
                                  full: 1
                                });
                                setSuccess('WhatsApp message sent successfully.');
                                setTimeout(() => setSuccess(null), 3000);
                                return;
                              } catch (apiError) {
                                console.warn('WhatsApp text API failed, falling back to manual mode:', apiError);
                              }
                            }
                            const backendUrl = process.env.REACT_APP_BACKEND_API_URL || 'https://dhandhapaani.store';

                            // Download PDF using the correct API endpoint
                            // Get clean token without Bearer prefix
                            let authToken = localStorage.getItem('authToken') || localStorage.getItem('accessToken') || '';
                            console.log('🔑 Raw authToken from localStorage:', authToken ? authToken.substring(0, 50) + '...' : 'none');

                            if (authToken.startsWith('Bearer ')) {
                              authToken = authToken.replace('Bearer ', '');
                              console.log('🔑 Stripped Bearer prefix, token now:', authToken.substring(0, 50) + '...');
                            }

                            console.log('🔑 Final Authorization header will be: Bearer', authToken.substring(0, 50) + '...');

                            const renderResp = await fetch(`${backendUrl}/api/invoices/${generatedInvoice.id}/download-pdf`, {
                              method: 'GET',
                              headers: {
                                'Authorization': `Bearer ${authToken}`,
                              }
                            });

                            if (!renderResp.ok) {
                              throw new Error(`PDF download failed: ${renderResp.status}`);
                            }

                            const blob = await renderResp.blob();
                            const file = new File([blob], `Invoice-${generatedInvoice.invoiceId}.pdf`, { type: 'application/pdf' });

                            const message = `🧾 INVOICE DETAILS\n\n` +
                              `📋 Invoice ID: ${generatedInvoice.invoiceId}\n` +
                              `👤 Customer: ${generatedInvoice.customerName}\n` +
                              `📅 Date: ${generatedInvoice.date}\n` +
                              `💰 Total Amount: ₹${generatedInvoice.subtotal?.toFixed(2) || '0.00'}\n` +
                              `✅ Amount Paid: ₹${generatedInvoice.totalPaid?.toFixed(2) || '0.00'}\n` +
                              `⏳ Amount Due: ₹${generatedInvoice.dueAmount?.toFixed(2) || '0.00'}\n` +
                              `💳 Payment Terms: ${generatedInvoice.paymentTerms || 'Net 30'}`;

                            // Try native share with attached file (mobile Chrome/Android)
                            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                              await navigator.share({
                                files: [file],
                                text: message,
                                title: `Invoice ${generatedInvoice.invoiceId}`
                              });
                              setSuccess('PDF shared via WhatsApp successfully!');
                              setTimeout(() => setSuccess(null), 3000);
                              return;
                            }

                            // Fallback: Download PDF and open WhatsApp with clear instructions
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `Invoice-${generatedInvoice.invoiceId}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);

                            // Enhanced WhatsApp message with clear instructions.


                            // Open WhatsApp after a short delay to ensure download completes
                            setTimeout(() => {
                              setError('Automatic WhatsApp send failed. Please check API/public URL configuration.');
                              setTimeout(() => setError(null), 5000);
                            }, 1000);
                          } catch (error) {
                            console.error('Error sharing via WhatsApp:', error);
                            setError('Failed to share via WhatsApp. Please try again.');
                          }
                        })();
                      }}
                      variant="contained"
                      startIcon={<WhatsAppIcon />}
                      size="medium"
                      sx={{
                        px: 2.2,
                        py: 1,
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                        boxShadow: '0 8px 20px rgba(37, 211, 102, 0.25)',
                        fontSize: '0.92rem',
                        fontWeight: 700,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #20c157 0%, #0f7b6b 100%)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 12px 28px rgba(37, 211, 102, 0.3)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Share via WhatsApp
                    </Button>
                  )}

                  <Button
                    onClick={async () => {
                      console.log('🔍 Print PDF - generatedInvoice:', generatedInvoice);
                      console.log('🔍 HTML Template length:', generatedInvoice?.htmlTemplate?.length);
                      console.log('🔍 html2pdf available:', typeof html2pdf);
                      const printProfile = resolveInvoicePrintProfile(templateData);

                      if (generatedInvoice?.htmlTemplate) {
                        try {
                          console.log('🖨️ Printing via browser using original HTML (pixel-perfect)');
                          // 1) Primary: print exact HTML in a new window (uses user's browser engine)
                          const htmlDoc = `<!doctype html><html><head><meta charset="utf-8" />
                            <meta name="viewport" content="width=device-width, initial-scale=1" />
                            <style>${getInvoicePrintCss(printProfile)}</style>
                          </head><body>${generatedInvoice.htmlTemplate}
                            <script>window.onload=function(){setTimeout(function(){window.focus();window.print();},50)}<\/script>
                          </body></html>`;
                          const _printWin1 = window.open('', '_blank');
                          if (_printWin1) {
                            _printWin1.document.open();
                            _printWin1.document.write(htmlDoc);
                            _printWin1.document.close();
                            return;
                          }

                          // 2) Backend renderer fallback (if popup blocked)
                          const backendUrl = process.env.REACT_APP_BACKEND_API_URL || 'https://dhandhapaani.store';
                          const renderResp = await fetch(`${backendUrl}/api/invoices/${generatedInvoice.id}/download-pdf`, {
                            method: 'GET',
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('accessToken') || ''}`,
                            }
                          });

                          if (renderResp.ok) {
                            const blob = await renderResp.blob();
                            const url = window.URL.createObjectURL(blob);
                            const win = window.open(url, '_blank');
                            if (win) {
                              win.onload = () => win.print();
                            }
                            return;
                          }

                          // 3) Fallback: pre-saved file
                          const fileName = generatedInvoice.pdfPath?.split(/[/\\]/).pop();
                          if (fileName) {
                            const response = await fetch(`${backendUrl}/api/invoice/print/${fileName}`, {
                              method: 'GET',
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('accessToken') || ''}`,
                                'Content-Type': 'application/json'
                              }
                            });
                            if (response.ok) {
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                            const _printWin2 = window.open(url, '_blank');
                            if (_printWin2) {
                              _printWin2.onload = () => {
                                _printWin2.print();
                              };
                            }
                              return;
                            }
                          }

                          // 4) Final fallback: client rendering
                          console.warn('⚠️ Print fallback to html2pdf.js');
                          const opt = getInvoiceHtml2PdfOptions(
                            printProfile,
                            `${generatedInvoice.invoiceId}.pdf`
                          );

                          const pdfBlob = await html2pdf().set(opt).from(generatedInvoice.htmlTemplate).outputPdf('blob');
                          const pdfUrl = URL.createObjectURL(pdfBlob);
                          const printWindow = window.open(pdfUrl, '_blank');
                          if (printWindow) {
                            printWindow.onload = () => {
                              printWindow.print();
                            };
                          }
                          console.log('✅ html2pdf.js screenshot fallback opened for printing');

                        } catch (error) {
                          console.error('❌ Error with backend PDF printing:', error);
                          console.error('❌ Error details:', error.message, error.stack);

                          // Final fallback to html2pdf.js screenshot
                          console.log('🔄 Final fallback to html2pdf.js screenshot for printing');
                          try {
                            const opt = getInvoiceHtml2PdfOptions(
                              printProfile,
                              `${generatedInvoice.invoiceId}.pdf`
                            );

                            const pdfBlob = await html2pdf().set(opt).from(generatedInvoice.htmlTemplate).outputPdf('blob');
                            const pdfUrl = URL.createObjectURL(pdfBlob);
                            const printWindow = window.open(pdfUrl, '_blank');
                            if (printWindow) {
                              printWindow.onload = () => {
                                printWindow.print();
                              };
                            }
                            console.log('✅ Final html2pdf.js fallback opened for printing');
                          } catch (fallbackError) {
                            console.error('❌ All PDF printing methods failed:', fallbackError);
                            setError('Failed to generate PDF for printing. Please try again.');
                          }
                        }
                      } else {
                        console.error('❌ No HTML template found for printing');
                        setError('No invoice template found. Please generate the invoice first.');
                      }
                    }}
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    size="medium"
                    sx={{
                      px: 2.2,
                      py: 1,
                      borderRadius: '10px',
                      borderColor: '#6b7280',
                      color: '#6b7280',
                      fontSize: '0.92rem',
                      fontWeight: 700,
                      '&:hover': {
                        borderColor: '#4b5563',
                        background: 'rgba(107, 114, 128, 0.04)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 8px 18px rgba(0,0,0,0.08)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Print Invoice
                  </Button>

                  <Button
                    onClick={() => {
                      setShowInvoice(false);
                      setGeneratedInvoice(null);
                      setPreviewInvoice(null);
                      setShowPreview(false);
                    }}
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    size="medium"
                    sx={{
                      px: 2.2,
                      py: 1,
                      borderRadius: '10px',
                      borderColor: '#ef4444',
                      color: '#ef4444',
                      fontSize: '0.92rem',
                      fontWeight: 700,
                      '&:hover': {
                        borderColor: '#dc2626',
                        background: 'rgba(239, 68, 68, 0.04)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 8px 18px rgba(239, 68, 68, 0.16)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Close
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Right Side - Simplified Setup Panel */}
        <Box sx={{
          minWidth: 0
        }}>
          <Card
            sx={{
              height: 'fit-content',
              borderRadius: '20px',
              border: '1px solid rgba(148, 163, 184, 0.28)',
              boxShadow: '0 18px 38px rgba(15, 23, 42, 0.08)',
              background: 'linear-gradient(170deg, #ffffff 0%, #f8fafc 100%)',
              position: { lg: 'sticky' },
              top: { lg: 84 }
            }}
          >
            <CardContent sx={{ p: 2.25 }}>
              <Typography variant="h6" gutterBottom>
                Invoice Setup
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Focused controls for fast invoice creation.
              </Typography>

              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={handleOpenDialog}
                fullWidth
                size="small"
                sx={{
                  mb: 2,
                  borderRadius: '10px',
                  fontWeight: 700,
                  py: 0.8,
                  borderColor: '#93c5fd',
                  color: '#1d4ed8',
                  '&:hover': {
                    borderColor: '#60a5fa',
                    backgroundColor: 'rgba(59, 130, 246, 0.08)'
                  }
                }}
              >
                Edit Invoice Settings
              </Button>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Invoice Number
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TextField
                    value={templateData.invoicePrefix || 'INV'}
                    InputProps={{ readOnly: true }}
                    sx={{
                      width: '90px',
                      '& .MuiInputBase-input': {
                        textAlign: 'center',
                        fontWeight: 600
                      }
                    }}
                    size="small"
                  />
                  <TextField
                    placeholder="001"
                    value={invoiceNumberInput}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/[^0-9]/g, '');
                      const limitedValue = numericValue.substring(0, 4);
                      setInvoiceNumberInput(limitedValue);
                      const fullInvoiceNumber = limitedValue ? `${templateData.invoicePrefix || 'INV'}${limitedValue}` : '';
                      handleInvoiceParamChange('invoiceNumber', fullInvoiceNumber);
                    }}
                    sx={{ flex: 1 }}
                    size="small"
                    inputProps={{ maxLength: 4 }}
                  />
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Example: {templateData.invoicePrefix || 'INV'}001
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Print Profile: {INVOICE_PRINT_PROFILES.find((p) => p.value === (templateData.printProfile || 'a4'))?.label || 'A4 (Standard)'}
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Customer
                </Typography>
                <Autocomplete
                  freeSolo
                  options={customerNames}
                  value={invoiceParams.customerName}
                  onChange={(event, newValue) => {
                    handleInvoiceParamChange('customerName', newValue || '');
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (event && event.type === 'change') {
                      handleInvoiceParamChange('customerName', newInputValue || '');
                    }
                  }}
                  loading={customerNames.length === 0}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search customer or type name"
                      helperText={customerNames.length === 0 ? 'Loading customers...' : `${customerNames.length} customers available`}
                    />
                  )}
                />
              </Box>

              <Button
                variant="contained"
                startIcon={<ReceiptIcon />}
                onClick={handleGenerateInvoice}
                disabled={generatingInvoice}
                fullWidth
                size="large"
                sx={{
                  borderRadius: '12px',
                  py: 1.15,
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
                  boxShadow: '0 10px 28px rgba(37, 99, 235, 0.28)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #4338ca 100%)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                {generatingInvoice ? (
                  <>
                    <CircularProgress size={18} sx={{ mr: 1, color: 'white' }} />
                    Generating...
                  </>
                ) : (
                  'Generate Invoice'
                )}
              </Button>

              <Typography variant="caption" color="textSecondary" sx={{ mt: 1.5, display: 'block' }}>
                {`${customerNames.length} customers available. You can also generate directly from the recent sales list below.`}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
      </>
      )}


      {/* Invoice Search and Filtering Section */}
      {activeTab === 'history' && (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Invoice History
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Search, filter, and manage generated invoices.
          </Typography>

          {/* Search Bar */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Search Invoices"
              placeholder="Search by invoice no, customer, phone, status, or amount..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearch} disabled={invoicesLoading}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
          </Box>

          {/* Filters Toggle */}
          <Box sx={{ mb: 2 }}>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterListIcon />}
              endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              variant="outlined"
              size="small"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            {(searchTerm || statusFilter || dateFrom || dateTo) && (
              <Button
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
                variant="text"
                size="small"
                sx={{ ml: 1 }}
                color="error"
              >
                Clear All
              </Button>
            )}
          </Box>

          {/* Advanced Filters */}
          <Collapse in={showFilters}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="partial">Partial Paid</MenuItem>
                    <MenuItem value="unpaid">Unpaid</MenuItem>
                    <MenuItem value="overdue">Overdue</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="From Date"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="To Date"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  disabled={invoicesLoading}
                  startIcon={<SearchIcon />}
                >
                  {invoicesLoading ? 'Searching...' : 'Search'}
                </Button>
              </Grid>
            </Grid>
          </Collapse>

          {/* Active Filters Display */}
          {(searchTerm || statusFilter || dateFrom || dateTo) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Active Filters:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {searchTerm && (
                  <Chip
                    label={`Search: "${searchTerm}"`}
                    onDelete={() => {
                      setSearchTerm('');
                      setCurrentPage(1);
                      loadInvoices(1, {
                        searchTerm: '',
                        statusFilter,
                        dateFrom,
                        dateTo
                      });
                    }}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {statusFilter && (
                  <Chip
                    label={`Status: ${statusFilter}`}
                    onDelete={() => {
                      setStatusFilter('');
                      setCurrentPage(1);
                      loadInvoices(1, {
                        searchTerm,
                        statusFilter: '',
                        dateFrom,
                        dateTo
                      });
                    }}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
                {dateFrom && (
                  <Chip
                    label={`From: ${dateFrom}`}
                    onDelete={() => {
                      setDateFrom('');
                      setCurrentPage(1);
                      loadInvoices(1, {
                        searchTerm,
                        statusFilter,
                        dateFrom: '',
                        dateTo
                      });
                    }}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
                {dateTo && (
                  <Chip
                    label={`To: ${dateTo}`}
                    onDelete={() => {
                      setDateTo('');
                      setCurrentPage(1);
                      loadInvoices(1, {
                        searchTerm,
                        statusFilter,
                        dateFrom,
                        dateTo: ''
                      });
                    }}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Results Summary */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {invoicesLoading ? 'Searching...' : `Found ${totalInvoices} invoice${totalInvoices !== 1 ? 's' : ''}`}
            </Typography>
          </Box>

          <Box
            sx={{
              mb: 3,
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
                xl: 'repeat(5, minmax(0, 1fr))'
              }
            }}
          >
            {historyStatusCards.map((card) => (
              <Paper
                key={card.key}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  borderColor: `${card.accent}33`,
                  background: card.bg
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em'
                  }}
                >
                  {card.label}
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 700, color: card.accent }}>
                  {formatINR(card.amount)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {card.count} invoice{card.count !== 1 ? 's' : ''}
                </Typography>
              </Paper>
            ))}
          </Box>

          {invoicesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : invoices.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  lg: 'minmax(0, 1.85fr) minmax(340px, 1fr)'
                },
                alignItems: 'start'
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderBottom: 1,
                      borderColor: 'divider',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: 'grey.50'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Invoices
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Page {currentPage}
                    </Typography>
                  </Box>

                  <TableContainer sx={{ maxHeight: 520 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Invoice</strong></TableCell>
                          <TableCell><strong>Customer</strong></TableCell>
                          <TableCell><strong>Date</strong></TableCell>
                          <TableCell align="right"><strong>Amount</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoices.map((invoice) => {
                          const invoiceKey = getInvoiceKey(invoice);
                          const isSelected = invoiceKey === selectedInvoiceKey;
                          const totalAmount = Number(invoice.total_amount || invoice.totalAmount || 0);
                          const effectiveStatus = getEffectiveInvoiceStatus(invoice);

                          return (
                            <TableRow
                              key={invoiceKey}
                              hover
                              selected={isSelected}
                              onClick={() => setSelectedInvoice(invoice)}
                              sx={{
                                cursor: 'pointer',
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(37, 99, 235, 0.10)'
                                },
                                '&.Mui-selected:hover': {
                                  backgroundColor: 'rgba(37, 99, 235, 0.16)'
                                }
                              }}
                            >
                              <TableCell sx={{ fontWeight: isSelected ? 700 : 500 }}>
                                {invoice.invoice_no || invoice.invoiceId || '--'}
                              </TableCell>
                              <TableCell>{invoice.customer_name || 'Walk-in Customer'}</TableCell>
                              <TableCell>{formatDateIN(invoice.invoice_date || invoice.date)}</TableCell>
                              <TableCell align="right">{formatINR(totalAmount)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={String(effectiveStatus).toUpperCase()}
                                  size="small"
                                  color={getInvoiceStatusChipColor(effectiveStatus)}
                                  variant={isSelected ? 'filled' : 'outlined'}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {totalInvoices > 20 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                      <Pagination
                        count={Math.ceil(totalInvoices / 20)}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}
                </Paper>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2.5,
                    p: 2.5,
                    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)'
                  }}
                >
                  {selectedInvoice ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}
                          >
                            Invoice Details
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {selectedInvoice.invoice_no || selectedInvoice.invoiceId || '--'}
                          </Typography>
                        </Box>
                        <Chip
                          label={String(selectedInvoiceStatus || 'unpaid').toUpperCase()}
                          color={getInvoiceStatusChipColor(selectedInvoiceStatus)}
                          variant="outlined"
                        />
                      </Box>

                      <Box
                        sx={{
                          mt: 0.5,
                          mb: 2,
                          display: 'grid',
                          gap: 1.5,
                          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
                        }}
                      >
                        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">Issue Date</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatDateIN(selectedInvoiceIssueDate)}
                          </Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">Due Date</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatDateIN(selectedInvoiceDueDate)}
                          </Typography>
                        </Paper>
                      </Box>

                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Customer
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {selectedInvoice.customer_name || 'Walk-in Customer'}
                        </Typography>
                        {selectedInvoice.customer_phone && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Phone: {selectedInvoice.customer_phone}
                          </Typography>
                        )}
                        {selectedInvoice.customer_email && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Email: {selectedInvoice.customer_email}
                          </Typography>
                        )}
                      </Paper>

                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Payment Summary
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                          <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatINR(selectedInvoiceTotal)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                          <Typography variant="body2" color="text.secondary">Total Paid</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatINR(selectedInvoicePaid)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Due Amount</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }} color={selectedInvoiceDue > 0 ? 'error.main' : 'success.main'}>
                            {formatINR(selectedInvoiceDue)}
                          </Typography>
                        </Box>
                      </Paper>

                      <Box
                        sx={{
                          mt: 2,
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))', lg: '1fr' },
                          gap: 1
                        }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<PictureAsPdfIcon />}
                          onClick={() => handleDownloadInvoicePdf(selectedInvoice)}
                          disabled={!selectedInvoice.id}
                        >
                          Download PDF
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PrintIcon />}
                          onClick={() => handlePrintInvoicePdf(selectedInvoice)}
                          disabled={!selectedInvoice.id}
                        >
                          Print
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<WhatsAppIcon />}
                          onClick={() => handleShareOnWhatsApp(selectedInvoice)}
                          disabled={!selectedInvoice.customer_phone}
                        >
                          WhatsApp
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <Box
                      sx={{
                        minHeight: 220,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        color: 'text.secondary'
                      }}
                    >
                      <ReceiptIcon sx={{ fontSize: 44, mb: 1, opacity: 0.5 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Select an invoice
                      </Typography>
                      <Typography variant="body2">
                        Choose an invoice from the list to view details and actions.
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
              <ReceiptIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                No invoices found
              </Typography>
              <Typography variant="body2">
                {searchTerm || statusFilter || dateFrom || dateTo
                  ? 'Try adjusting your search criteria'
                  : 'You have no invoices yet. Generate your first invoice from recent sales.'
                }
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      )}

      {/* Recent Sales Section - Moved to Bottom */}
      {activeTab === 'create' && (
      <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          Most Recent Sales (Last 10)
        </Typography>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={async () => {
            try {
              setSalesLoading(true);
              // Clear all caches
              dataCache.clear();
              console.log('🧹 Cleared all data caches');

              // Force fresh API call
              const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL || 'https://dhandhapaani.store'}/api/sales`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                  'Content-Type': 'application/json'
                }
              });

              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              const result = await response.json();
              const data = result.sales || result || [];
              data.sort((a, b) => (a.id || 0) - (b.id || 0));

              const salesWithStatus = data.map(sale => ({
                ...sale,
                invoice_generated: false,
                existing_invoice: null,
                invoice_check_performed: false,
                invoice_check_timestamp: new Date().toISOString()
              }));

              setSales(salesWithStatus);
              console.log('✅ Sales data refreshed with fresh API call');

              // Update customer names from fresh sales data
              const salesCustomerNames = [...new Set(
                salesWithStatus
                  .map(sale => sale.customer_name)
                  .filter(name => name && name.trim() !== '')
                  .map(name => name.trim())
              )].sort();

              if (salesCustomerNames.length > 0) {
                setCustomerNames(salesCustomerNames);
                console.log('👥 Updated customer names from fresh sales data:', salesCustomerNames);
              }

              setSuccess('Sales data refreshed successfully!');
              setTimeout(() => setSuccess(null), 2000);
            } catch (error) {
              console.error('Error refreshing sales data:', error);
              setError('Failed to refresh sales data');
              setTimeout(() => setError(null), 3000);
            } finally {
              setSalesLoading(false);
            }
          }}
          disabled={generatingInvoice || salesLoading}
          variant="outlined"
        >
          {salesLoading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        {salesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
              Loading recent sales...
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice ID</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>WhatsApp</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {recentUniqueSales.map((sale, index) => {
              const prefix = templateData.invoicePrefix || 'INV';
              const invoiceId = sale['Invoice No'] || sale.invoice_no || `${prefix}${(index + 1).toString().padStart(3, '0')}`;

              return (
                <TableRow key={sale.id || sale['Invoice No'] || index}>
                  <TableCell>{invoiceId}</TableCell>
                  <TableCell>{sale['Date'] || sale.invoice_date || sale.date || 'No date'}</TableCell>
                  <TableCell>{sale['Customer Name'] || sale.customer_name || 'Unknown Customer'}</TableCell>
                  <TableCell>
                    <Box sx={{ minWidth: 180, maxWidth: 220 }}>
                      {(() => {
                        const items = sale.items || [];

                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {items.map((item, i) => {
                              const quantity = parseFloat(item.quantity) || 1;
                              return (
                                <Chip
                                  key={i}
                                  label={`${item.item_name || item.name || 'Unknown Item'} (${quantity})`}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#333',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    height: '30px'
                                  }}
                                />
                              );
                            })}
                          </Box>
                        );
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell>₹{parseFloat(sale['Total Amount'] || sale['Final Amount'] || sale.total_amount || sale.final_amount || 0).toLocaleString('en-IN')}</TableCell>
                 <TableCell>
                   {sale.whatsapp_link ? (
                     <Button
                       size="small"
                       startIcon={<WhatsAppIcon />}
                       href={sale.whatsapp_link}
                       target="_blank"
                       variant="outlined"
                       color="success"
                     >
                       Share
                     </Button>
                   ) : (
                     <Typography variant="body2" color="textSecondary">Not available</Typography>
                   )}
                 </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                    {/* Enhanced Invoice Status Indicator */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                      {sale.invoice_generated ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          <Box>
                            <Typography variant="caption" color="success.main" sx={{ fontWeight: 600, display: 'block' }}>
                              Invoice Generated
                            </Typography>
                            {sale.existing_invoice && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {sale.existing_invoice.invoice_no}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ) : sale.invoice_check_performed === false ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="warning.main" sx={{ fontStyle: 'italic' }}>
                            Checking...
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Not Generated
                          </Typography>
                        </Box>
                      )}

                      {/* Show error if check failed */}
                      {sale.invoice_check_error && (
                        <Typography variant="caption" color="error.main" sx={{ fontSize: '0.7rem' }}>
                          Check failed
                        </Typography>
                      )}
                    </Box>

                                        {/* Action Buttons */}
                    {sale.invoice_generated ? (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() =>
                            handleViewInvoicePdf({
                              id: sale.existing_invoice?.id || sale.id,
                              invoice_no: sale.existing_invoice?.invoice_no || sale.invoice_no
                            })
                          }
                          variant="contained"
                          color="success"
                          disabled={generatingInvoice}
                        >
                          View Invoice
                        </Button>
                        <Button
                          size="small"
                          startIcon={<RefreshIcon />}
                          onClick={() => handleGenerateInvoiceFromRecentSale(sale, true)}
                          variant="outlined"
                          color="secondary"
                          disabled={generatingInvoice}
                        >
                          {generatingInvoice ? 'Regenerating...' : 'Regenerate'}
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        startIcon={<ReceiptIcon />}
                        onClick={() => handleGenerateInvoiceFromRecentSale(sale, false)}
                        variant="contained"
                        color="primary"
                        disabled={generatingInvoice}
                      >
                        {generatingInvoice ? 'Generating...' : 'Generate Invoice'}
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
              );
            })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      </>
      )}


      {/* Template Customization Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Customize Invoice Template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Customize your invoice with essential settings
          </Typography>

          <Grid container spacing={2}>
            {/* Essential Fields Only */}

            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Tax Rate (%)"
                type="number"
                fullWidth
                variant="outlined"
                value={templateData.taxRate}
                onChange={(e) => handleTemplateChange('taxRate', e.target.value)}
                helperText="Tax rate to apply to final amount"
                inputProps={{ min: 0, max: 100, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Invoice Prefix"
                fullWidth
                variant="outlined"
                value={templateData.invoicePrefix}
                onChange={(e) => handleTemplateChange('invoicePrefix', e.target.value)}
                helperText="e.g., INV, INV001, or business initials like TS"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Authorized Signature"
                fullWidth
                variant="outlined"
                value={templateData.authorizedSignature}
                onChange={(e) => handleTemplateChange('authorizedSignature', e.target.value)}
                helperText="Name for authorized signature"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Payment Terms"
                fullWidth
                variant="outlined"
                value={templateData.paymentTerms}
                onChange={(e) => handleTemplateChange('paymentTerms', e.target.value)}
                helperText="e.g., Net 30, Due on Receipt, COD"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl margin="dense" fullWidth>
                <InputLabel id="invoice-print-profile-label">Print Profile</InputLabel>
                <Select
                  labelId="invoice-print-profile-label"
                  value={templateData.printProfile || 'a4'}
                  label="Print Profile"
                  onChange={(e) => handleTemplateChange('printProfile', e.target.value)}
                >
                  {INVOICE_PRINT_PROFILES.map((profile) => (
                    <MenuItem key={profile.value} value={profile.value}>
                      {profile.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="textSecondary">
                Use thermal profiles for slip printers.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Sales Note"
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                value={templateData.salesNote}
                onChange={(e) => handleTemplateChange('salesNote', e.target.value)}
                helperText="Additional notes for the invoice"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={updatingTemplate}>Cancel</Button>
          <Button
            onClick={handleUpdateTemplate}
            variant="contained"
            disabled={updatingTemplate}
            startIcon={updatingTemplate ? <CircularProgress size={16} /> : null}
          >
            {updatingTemplate ? 'Updating...' : 'Update Template'}
          </Button>
        </DialogActions>
      </Dialog>



      {/* Customer Invoice Selection Dialog */}
      <Dialog
        open={showCustomerInvoiceSelector}
        onClose={() => setShowCustomerInvoiceSelector(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Select Invoice for {selectedCustomerName}
          <Typography variant="body2" color="textSecondary">
            This customer has multiple invoices. Please select which one to generate.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice ID</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Total Purchase</TableCell>
                  <TableCell>Amount Paid</TableCell>
                  <TableCell>Amount Due</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customerInvoices.map((invoice, index) => (
                  <TableRow key={index}>
                    <TableCell>{invoice.summary.invoiceId}</TableCell>
                    <TableCell>{invoice.summary.date}</TableCell>
                    <TableCell>₹{invoice.summary.totalPurchase.toLocaleString('en-IN')}</TableCell>
                    <TableCell>₹{invoice.summary.totalPaid.toLocaleString('en-IN')}</TableCell>
                    <TableCell>
                      <Typography
                        color={invoice.summary.totalDue > 0 ? 'error' : 'success'}
                        fontWeight="bold"
                      >
                        ₹{invoice.summary.totalDue.toLocaleString('en-IN')}
                      </Typography>
                    </TableCell>
                    <TableCell>{invoice.summary.itemsCount} items</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<ReceiptIcon />}
                        onClick={async () => {
                          try {
                            setGeneratingInvoice(true);
                            setShowCustomerInvoiceSelector(false);
                            setError(null);

                            console.log('🎯 Generating selected customer invoice:', invoice.summary.invoiceId);

                            // Call backend for PDF generation
                            const invoiceData = {
                              rowNumber: invoice.rowNumber,
                              saleRow: invoice.saleRow,
                              templateData: templateData,
                              selectedTemplate: templateData.theme || 'simple'
                            };

                            const result = await backendApi.generateInvoice(invoiceData);

                            // Store the generated invoice from backend response
                            if (result.invoice) {
                              setGeneratedInvoice({
                                ...result.invoice,
                                htmlTemplate: result.invoice.htmlTemplate || '' // Use HTML from backend generateInvoice response
                              });
                              setShowInvoice(true);
                            }

                            setSuccess('Invoice generated successfully!');
                            setTimeout(() => setSuccess(null), 3000);

                            // Refresh sales data to update the invoice status
                            await refreshSalesData();

                          } catch (err) {
                            setError('Failed to generate invoice. Please try again.');
                            console.error('Error generating customer invoice:', err);
                          } finally {
                            setGeneratingInvoice(false);
                          }
                        }}
                        disabled={generatingInvoice}
                      >
                        {generatingInvoice ? 'Generating...' : 'Generate'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {/* Summary Row */}
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                    TOTAL SUMMARY
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '16px', color: 'primary.main' }}>
                    ₹{customerInvoiceTotals.totalPurchase.toLocaleString('en-IN')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '16px', color: 'success.main' }}>
                    ₹{customerInvoiceTotals.totalPaid.toLocaleString('en-IN')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '16px', color: customerInvoiceTotals.totalDue > 0 ? 'error.main' : 'success.main' }}>
                    ₹{customerInvoiceTotals.totalDue.toLocaleString('en-IN')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                    {customerInvoiceTotals.totalItems} items
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={() => {
                          // Generate customer summary PDF
                          const summaryHtml = generateCustomerSummaryHTML(customerInvoices, selectedCustomerName);
                          const printWindow = window.open('', '_blank');
                          printWindow.document.write(`
                            <html>
                              <head>
                                <title>Customer Summary - ${selectedCustomerName}</title>
                                <style>
                                  body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
                                  .summary-content { max-width: 800px; margin: 0 auto; }
                                  table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                                  th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
                                  th { background-color: #f5f5f5; font-weight: bold; }
                                  .total-row { background-color: #e8f4fd; font-weight: bold; }
                                  .due-amount { color: #d32f2f; font-weight: bold; }
                                  @media print {
                                    body { margin: 0; }
                                    .summary-content { page-break-inside: avoid; }
                                  }
                                </style>
                              </head>
                              <body>
                                <div class="summary-content">
                                  ${summaryHtml}
                                </div>
                              </body>
                            </html>
                          `);
                          printWindow.document.close();
                          printWindow.print();
                        }}
                      >
                        Print Summary
                      </Button>
                      {customerInvoices[0]?.invoiceData?.phone && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<WhatsAppIcon />}
                          color="success"
                          onClick={async () => {
                            const totalPurchase = customerInvoiceTotals.totalPurchase;
                            const totalPaid = customerInvoiceTotals.totalPaid;
                            const totalDue = customerInvoiceTotals.totalDue;
                            // Ensure phone is a string before using string methods
                            const phoneValue = customerInvoices[0].invoiceData.phone;
                            const phone = (phoneValue && typeof phoneValue === 'string')
                              ? phoneValue.replace(/\D/g, '')
                              : (phoneValue ? String(phoneValue).replace(/\D/g, '') : '');

                            const summaryMessage = encodeURIComponent(
                              `📊 Customer Summary - ${selectedCustomerName}\n\n` +
                              `💰 Total Purchase: ₹${totalPurchase.toLocaleString('en-IN')}\n` +
                              `✅ Total Paid: ₹${totalPaid.toLocaleString('en-IN')}\n` +
                              `⏳ Total Due: ₹${totalDue.toLocaleString('en-IN')}\n\n` +
                              `📝 Invoice Details:\n` +
                              customerInvoices.map((inv, idx) =>
                                `${idx + 1}. ${inv.summary.invoiceId} - ₹${inv.summary.totalPurchase.toLocaleString('en-IN')} (Due: ₹${inv.summary.totalDue.toLocaleString('en-IN')})`
                              ).join('\n') +
                              `\n\nPlease find the detailed summary attached.`
                            );

                            const plainSummaryMessage = decodeURIComponent(summaryMessage);
                            try {
                              await backendApi.sendWhatsAppText({
                                number: phone,
                                message: plainSummaryMessage,
                                footer: businessData?.companyName || businessData?.name || 'Dhandhapaani',
                                full: 1
                              });
                              setSuccess('Customer summary sent on WhatsApp successfully.');
                              setTimeout(() => setSuccess(''), 3000);
                            } catch (apiError) {
                              console.warn('WhatsApp summary API send failed:', apiError);
                              setError('WhatsApp summary send failed. Please check server logs/config.');
                              setTimeout(() => setError(''), 4000);
                            }
                          }}
                        >
                          Share Summary
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCustomerInvoiceSelector(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog
        open={showStatusDialog}
        onClose={() => setShowStatusDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Update Invoice Status
          {selectedInvoice && (
            <Typography variant="body2" color="textSecondary">
              Invoice: {selectedInvoice.invoice_no || selectedInvoice.invoiceId}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="viewed">Viewed</MenuItem>
                <MenuItem value="downloaded">Downloaded</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="partial">Partial Payment</MenuItem>
                <MenuItem value="unpaid">Unpaid</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (Optional)"
              placeholder="Add notes about this status change..."
              value={statusNotes}
              onChange={(e) => setStatusNotes(e.target.value)}
              helperText="These notes will be saved in the invoice history"
            />
          </Box>
        </DialogContent>
        {/* DISABLED: Status update dialog not needed */}
        {/* <DialogActions>
          <Button onClick={() => setShowStatusDialog(false)}>Cancel</Button>
          <Button
            onClick={handleStatusUpdate}
            variant="contained"
            disabled={updatingStatus || !newStatus}
            startIcon={updatingStatus ? <CircularProgress size={16} /> : null}
          >
            {updatingStatus ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions> */}
      </Dialog>

      {/* Status History Dialog */}
      <Dialog
        open={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Invoice Status History
          {selectedInvoice && (
            <Typography variant="body2" color="textSecondary">
              Invoice: {selectedInvoice.invoice_no || selectedInvoice.invoiceId}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : invoiceHistory.length > 0 ? (
            <Box>
              {invoiceHistory.map((entry, index) => (
                <Card key={entry.id || index} sx={{ mb: 2 }}>
                  <CardContent sx={{ pb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {entry.previousStatus ? `${entry.previousStatus} → ${entry.newStatus}` : `Set to ${entry.newStatus}`}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {entry.changedByName || entry.changedBy || 'System'} • {new Date(entry.changedAt || entry.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                      <Chip
                        label={entry.newStatus}
                        size="small"
                        color={
                          entry.newStatus === 'paid' ? 'success' :
                          entry.newStatus === 'partial' ? 'warning' :
                          entry.newStatus === 'overdue' ? 'error' :
                          'default'
                        }
                      />
                    </Box>
                    {entry.notes && (
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        "{entry.notes}"
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
              <Typography variant="body2">
                No status history available for this invoice.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistoryDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Preview Dialog */}
      <Dialog
        open={fullScreenPreview}
        onClose={() => setFullScreenPreview(false)}
        maxWidth={false}
        fullWidth
        fullScreen
        sx={{
          '& .MuiDialog-paper': {
            background: '#f8fafc',
            borderRadius: 0
          }
        }}
      >
        <DialogContent sx={{ p: 0, height: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{
            p: 3,
            background: 'white',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Fullscreen Preview - {showInvoice ? generatedInvoice?.invoiceId : 'Template Preview'}
            </Typography>
            <IconButton onClick={() => setFullScreenPreview(false)} size="large">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, p: 4, overflow: 'auto', background: '#f8fafc' }}>
            <Box sx={{
              maxWidth: '800px',
              mx: 'auto',
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 25px 80px rgba(0,0,0,0.15)',
              p: 4,
              minHeight: '100%'
            }}>
              {showInvoice && generatedInvoice?.htmlTemplate ? (
                <div
                  dangerouslySetInnerHTML={{ __html: generatedInvoice.htmlTemplate }}
                  style={{ width: '100%' }}
                />
              ) : showPreview && previewInvoice ? (
                <div
                  dangerouslySetInnerHTML={{ __html: previewInvoice.htmlTemplate }}
                  style={{ width: '100%' }}
                />
              ) : (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '400px',
                  flexDirection: 'column',
                  textAlign: 'center'
                }}>
                  <ReceiptIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5, color: '#9ca3af' }} />
                  <Typography variant="h6" gutterBottom sx={{ color: '#1a202c', fontWeight: 600 }}>
                    No Preview Available
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    Generate an invoice or select a template to see the preview
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Success Animation Overlay */}
      {showSuccessAnimation && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(16, 185, 129, 0.95)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          animation: 'fadeIn 0.5s ease'
        }}>
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <Box sx={{
              width: 120,
              height: 120,
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              animation: 'bounce 0.6s ease'
            }}>
              <CheckCircleIcon sx={{ fontSize: 60 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
              Invoice Generated!
            </Typography>
            <Typography variant="h5" sx={{ opacity: 0.9 }}>
              Your professional invoice is ready to share
            </Typography>
          </Box>
        </Box>
      )}

    </Box>
  );
}

export default Invoices;










